import { useState, useEffect } from 'react';
import { RatingBar } from './rating-bar.jsx';
import { RotterdamForm } from './rotterdam-form.jsx';
import { PESTS, STEDEN, cityForPostcode, REVIEWS, BUSINESS_STATS, OPENING_HOURS } from './data.js';
import { IconPhone, IconCheck, IconShield, IconPin, IconStar, IconMail, IconArrow, IconCity, PestDrawing } from './icons.jsx';
import { useSEO, buildBreadcrumb } from './seo.jsx';
import { url, navigate } from './url.js';
import { GOOGLE_REVIEWS_FALLBACK, fetchGoogleReviews, getGoogleAggregate } from './google-reviews.js';
import { ReviewsSlider } from './reviews-slider.jsx';
import { PlaagCard } from './andere-diensten-carousel.jsx';
import { PartnerCompactForm } from './compact-forms.jsx';
import { submitForm } from './submit-form.js';

// Live Google-aggregaat hook — fallback uit data.js voor prerender, daarna
// upgrade naar live cijfers via Cloudflare Worker → Google Places.
function useGoogleAggregate() {
  const [agg, setAgg] = useState({ rating: REVIEWS.rating, total: REVIEWS.total });
  useEffect(() => {
    let alive = true;
    getGoogleAggregate().then(data => {
      if (!alive || !data) return;
      if (data.rating !== agg.rating || data.total !== agg.total) {
        setAgg({ rating: data.rating, total: data.total });
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return agg;
}

// ──────────────────────────────────────────────────────────────────────
// Fixed-route pages: SpoedPage, BlogPage, ReviewsPage, ContactPage,
// OffertePage, NotFoundPage.
// ──────────────────────────────────────────────────────────────────────

// ─── Reusable hero blocks — used to match the home page hero on every page ──
// HeroPostcodeBar = ALLEEN de zoekbalk (postcode-box). Hierdoor heeft élke
// pagina 100% identiek dezelfde zoekbalk (zelfde hoogte, breedte, styling).
// Trust-items per pagina blijven context-specifiek en staan ernaast.
export function HeroPostcodeBar({ pc, setPc, formatPc, searchPostcode }) {
  return (
    <div className="postcode-box">
      <div className="pc-wrap">
        <IconPin />
        <input type="text" className="pc-input" value={pc}
          onChange={(e) => setPc(formatPc(e.target.value))}
          onKeyDown={(e) => { if (e.key === 'Enter') searchPostcode(); }}
          placeholder="Jouw postcode, bijv. 2512 AB" maxLength={7} autoComplete="off" />
      </div>
      <button className="btn-zoek" onClick={searchPostcode}>Bekijk prijs &amp; beschikbaarheid →</button>
    </div>
  );
}

// HeroPostcodeAndTrust = zoekbalk + standaard 4 trust-items.
// Gebruikt op pagina's waar generieke trust-statements passen (Reviews, Contact).
// Mobile rating-bar tussen postcode en USPs voor consistentie met home-hero.
function HeroPostcodeAndTrust({ pc, setPc, formatPc, searchPostcode }) {
  return (
    <>
      <HeroPostcodeBar pc={pc} setPc={setPc} formatPc={formatPc} searchPostcode={searchPostcode} />
      <div className="hero-rating-mobile"><RatingBar /></div>
      <div className="hero-trust">
        <div className="tit"><span className="tit-mark">✓</span> Spoeddienst 24/7</div>
        <div className="tit"><span className="tit-mark">✓</span> Snel ter plaatse</div>
        <div className="tit"><span className="tit-mark">✓</span> Vaste prijs vooraf</div>
        <div className="tit"><span className="tit-mark">✓</span> Particulier &amp; zakelijk</div>
      </div>
    </>
  );
}

export function SpoedPage() {
  const [openFaq, setOpenFaq] = useState(0);
  // FAQ-array als const zodat het ook in FAQPage JSON-LD kan voor rich snippets.
  // Garantie-FAQ verduidelijkt nu expliciet waarom we voor andere plagen geen
  // garantie kunnen geven (externe factoren), conform de eerlijke positie die
  // ook op plaag- en stad-pagina's wordt gehanteerd.
  const SPOED_FAQS = [
    { q: "Wat valt onder spoed en wat niet?",
      a: "Spoed-gevallen zijn situaties waarin wachten op een normale afspraak onverantwoord is: een wespennest binnen 2 meter van kinderen, ratten in keuken of voorraad, hoornaars-nest in tuin, plotselinge vliegenzwerm (vaak duidt op grotere bron zoals dood dier), of een aankomende NVWA-controle in horeca. Voor niet-urgente situaties (1 muizenkeutel, paar mieren in keuken) is een normale planning voldoende en bespaar je op spoed-tarieven." },
    { q: "Wat is de reactietijd in mijn stad?",
      a: "In Den Haag, Rotterdam en directe omgeving zijn wij meestal binnen 4-6 uur ter plaatse bij spoed. Voor de rest van Zuid-Holland, Noord-Holland en Utrecht hanteren wij maximaal 24 uur als spoed-norm. Bij echte noodgevallen (allergie, gezondheidsrisico) maken wij vrijwel altijd uitzonderingen en rijden wij direct uit." },
    { q: "Wat kost een spoed-behandeling 's nachts?",
      a: "Voor behandelingen tussen 22:00 en 07:00 hanteren wij een toeslag tot 100% bovenop ons reguliere tarief. De exacte toeslag is afhankelijk van de afstand tot de dichtstbijzijnde bestrijder, hoe verder weg, hoe hoger de toeslag. Wij vermelden dit altijd vooraf telefonisch, geen verrassingen op de factuur." },
    { q: "Hoe snel kan ik een spoed-aanvraag doen?",
      a: "Voor échte spoed adviseren wij altijd bellen op 085 500 50 50, sneller dan typen en wij beoordelen direct of er een uitruk-noodzaak is. Voor minder urgente zaken kan via het formulier of e-mail, dan reageren wij binnen 30 minuten op werkdagen." },
    { q: "Werken jullie ook op zondag en feestdagen?",
      a: "Ja, ons spoed-team is 24/7 oproepbaar, ook op zon- en feestdagen. Tijdens deze uren rekenen wij geen extra avond/nacht-toeslag voor reguliere uitruk, alleen voor uitruk tussen 22:00 en 07:00 hanteren wij een nacht-toeslag tot 100% (afhankelijk van afstand tot de bestrijder)." },
    { q: "Geven jullie garantie op de behandeling?",
      a: "Op wespennesten wel, op overige plagen niet. De reden: te veel externe factoren zijn onvoorspelbaar, denk aan nieuwe bronnen vanuit de buren, eitjes die later uitkomen, of een warmere zomer die de populatie weer aanjaagt. Wat wij wél bieden: vakkundige bron-aanpak met CTGB-gecertificeerde middelen, nacontrole na 2-4 weken bij hardnekkige plagen, en eerlijke preventie-adviezen. Bij wespennesten geven wij 100% seizoensgarantie: keert het nest binnen het seizoen terug, dan behandelen wij kosteloos opnieuw." },
  ];

  useSEO({
    title: "Spoed ongediertebestrijding 24/7",
    description: "Spoedhulp bij ongedierte? ABC is 24/7 bereikbaar. Binnen 24 uur op locatie in ZH, NH en Utrecht. Bel direct 085 500 50 50, geen wachtmuziek.",
    path: "/spoedservice",
    jsonLd: [
      buildBreadcrumb([{ name: "Home", url: "/" }, { name: "Spoedservice", url: "/spoedservice" }]),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": SPOED_FAQS.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
    ],
  });

  const [pc, setPc] = useState("");
  const formatPc = (raw) => { let v = raw.replace(/\s/g, '').toUpperCase(); if (v.length > 4) v = v.slice(0, 4) + ' ' + v.slice(4, 6); return v; };
  const searchPostcode = () => {
    const r = pc.trim().replace(/\s/g, '');
    if (r.length < 4) { alert('Vul een geldige postcode in'); return; }
    const city = cityForPostcode(r);
    navigate(city ? `/werkgebied/${city}?pc=${encodeURIComponent(r)}` : `/offerte?pc=${encodeURIComponent(r)}`);
  };
  const [hf, setHf] = useState({ naam: "", telefoon: "", postcode: "", ongedierte: "" });
  const [hfSent, setHfSent] = useState(false);
  const updHf = k => e => setHf(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="page-anim">
      <section className="hero">
        <div className="container">
          <div className="hero-inner">
            <div>
              <div className="breadcrumb" style={{ marginBottom: 18 }}>
                <a href={url.home()}>Home</a><span className="sep">/</span>
                <span style={{ color: 'rgba(255,255,255,.9)' }}>Spoedservice</span>
              </div>
              <div className="hero-badge">🚨 24/7 Bereikbaar</div>
              <h1>Spoedhulp nodig? <br/><em>Vandaag</em> nog geholpen.</h1>
              <p className="hero-sub">Wespennest of ratten in de keuken? Bel ons direct, een vakman pakt op, persoonlijk contact zonder wachten.</p>
              <HeroPostcodeBar pc={pc} setPc={setPc} formatPc={formatPc} searchPostcode={searchPostcode} />
              <div className="hero-rating-mobile"><RatingBar /></div>
              <div className="hero-trust">
                <div className="tit"><span className="tit-mark">✓</span> 24/7 bereikbaar</div>
                <div className="tit"><span className="tit-mark">✓</span> ≤ 24u op locatie</div>
                <div className="tit"><span className="tit-mark">✓</span> Direct contact</div>
                <div className="tit"><span className="tit-mark">✓</span> Vaste prijs</div>
              </div>
              <div className="hero-rating-desktop"><RatingBar /></div>
            </div>
            <RotterdamForm variant="hero" />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="sh">
            <div className="section-tag">Wanneer spoed?</div>
            <h2>Bij deze situaties bellen mensen vaak <em>direct</em></h2>
          </div>
          <div className="usp-grid spoed-grid">
            {[
            { t: "Wespennest", d: "Bij spelende kinderen, allergie in huis, of nest binnen 2 meter van de voordeur.", tone: "yellow", icon: (
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="14" cy="14" rx="10" ry="5" fill="#FBBF24" opacity=".55" transform="rotate(-25 14 14)"/>
                <ellipse cx="34" cy="14" rx="10" ry="5" fill="#FBBF24" opacity=".55" transform="rotate(25 34 14)"/>
                <ellipse cx="24" cy="28" rx="9" ry="13" fill="#FBBF24"/>
                <rect x="16" y="24" width="16" height="3" rx="1" fill="#1B2A4A"/>
                <rect x="16" y="33" width="16" height="3" rx="1" fill="#1B2A4A"/>
                <ellipse cx="24" cy="18" rx="7" ry="6" fill="#1B2A4A"/>
                <polygon points="24,46 21,40 27,40" fill="#1B2A4A"/>
              </svg>
            ) },
            { t: "Ratten in huis", d: "Volksgezondheidsrisico. Niet wachten, meldplicht, verspreiden ziektes.", tone: "red", icon: (
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M38 30 Q46 28 46 22 Q46 16 40 14" stroke="#7A5A35" strokeWidth="2.4" fill="none" strokeLinecap="round"/>
                <ellipse cx="26" cy="30" rx="16" ry="10" fill="#7A5A35"/>
                <circle cx="11" cy="24" r="9" fill="#9C7B52"/>
                <circle cx="7" cy="16" r="4.5" fill="#7A5A35"/>
                <circle cx="15" cy="14" r="4.5" fill="#7A5A35"/>
                <circle cx="9" cy="22" r="1.8" fill="#1B2A4A"/>
                <circle cx="14" cy="21" r="1.8" fill="#1B2A4A"/>
                <ellipse cx="3" cy="26" rx="1.8" ry="1.4" fill="#FF6B6B"/>
              </svg>
            ) },
            { t: "Hoornaars / Aziatische hoornaar", d: "Agressief, gevaarlijke steken. Niet zelf benaderen, bel ons.", tone: "danger", icon: (
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="22" fill="#DC2626" opacity=".10"/>
                <path d="M24 8 L36 14 V26 C36 34 30 38 24 40 C18 38 12 34 12 26 V14 Z" fill="#DC2626"/>
                <path d="M24 14 V32 M16 22 H32" stroke="white" strokeWidth="2.4" strokeLinecap="round"/>
                <circle cx="24" cy="22" r="1.8" fill="white"/>
              </svg>
            ) },
            { t: "NVWA-controle aankomend", d: "Horeca-overlast moet opgelost zijn vóór de inspectie. Wij staan paraat.", tone: "blue", icon: (
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="8" y="6" width="32" height="38" rx="4" fill="#3B82F6" opacity=".15"/>
                <rect x="8" y="6" width="32" height="38" rx="4" stroke="#3B82F6" strokeWidth="2.2"/>
                <line x1="14" y1="16" x2="34" y2="16" stroke="#3B82F6" strokeWidth="2.2" strokeLinecap="round"/>
                <line x1="14" y1="23" x2="30" y2="23" stroke="#3B82F6" strokeWidth="2.2" strokeLinecap="round"/>
                <path d="M14 32 L20 38 L34 24" stroke="#16A34A" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) },
            { t: "Plotselinge vliegenzwerm", d: "Vaak duidt op grotere bron, dood dier, lekkage. Snel handelen.", tone: "purple", icon: (
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="14" cy="14" rx="5" ry="3" fill="#A78BFA" transform="rotate(-20 14 14)"/>
                <ellipse cx="34" cy="12" rx="5" ry="3" fill="#A78BFA" transform="rotate(20 34 12)"/>
                <ellipse cx="10" cy="32" rx="5" ry="3" fill="#7C3AED" transform="rotate(20 10 32)"/>
                <ellipse cx="38" cy="34" rx="5" ry="3" fill="#7C3AED" transform="rotate(-20 38 34)"/>
                <ellipse cx="24" cy="24" rx="9" ry="7" fill="#7C3AED"/>
                <ellipse cx="20" cy="22" rx="3" ry="2.4" fill="rgba(173,200,255,.75)" transform="rotate(-25 20 22)"/>
                <ellipse cx="28" cy="22" rx="3" ry="2.4" fill="rgba(173,200,255,.75)" transform="rotate(25 28 22)"/>
              </svg>
            ) },
            { t: "Bedwantsen na reis", d: "Vermenigvuldigen razendsnel. Vandaag bestrijden = morgen geen plaag.", tone: "brown", icon: (
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="20" width="36" height="14" rx="3" fill="#92400E"/>
                <rect x="6" y="16" width="36" height="6" rx="2" fill="#B45309"/>
                <line x1="6" y1="14" x2="6" y2="36" stroke="#78350F" strokeWidth="3" strokeLinecap="round"/>
                <line x1="42" y1="14" x2="42" y2="36" stroke="#78350F" strokeWidth="3" strokeLinecap="round"/>
                <ellipse cx="18" cy="22" rx="3" ry="2.4" fill="#1B2A4A"/>
                <ellipse cx="30" cy="22" rx="3" ry="2.4" fill="#1B2A4A"/>
                <line x1="6" y1="36" x2="6" y2="42" stroke="#78350F" strokeWidth="3" strokeLinecap="round"/>
                <line x1="42" y1="36" x2="42" y2="42" stroke="#78350F" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            ) }].
            map((c) =>
            <div key={c.t} className={`usp-card spoed-card spoed-tone-${c.tone}`}>
                <div className="usp-icon spoed-icon">{c.icon}</div>
                <div>
                  <h3>{c.t}</h3>
                  <p>{c.d}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="section section-bg">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'center' }} className="sp-info">
            <div>
              <div className="section-tag">Hoe het werkt</div>
              <h2 className="display-l" style={{ fontSize: 'clamp(1.8rem,3vw,2.4rem)', fontWeight: 800, color: 'var(--navy)', margin: '14px 0 24px' }}>
                Eén telefoontje en we komen eraan!
              </h2>
              <ol style={{ paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 18 }}>
                {[
                ["U belt", "Wij pakken binnen 11 seconden gemiddeld op. Een vakman aan de lijn.", "#22C55E", "linear-gradient(135deg, #DCFCE7, #BBF7D0)", "linear-gradient(135deg, #F0FDF4, #DCFCE7)", (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#15803D" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.64 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l.81-.81a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 17z"/>
                  </svg>
                )],
                ["Wij beoordelen", "Korte intake aan de telefoon, wat speelt er, waar, hoe urgent.", "#3B82F6", "linear-gradient(135deg, #DBEAFE, #93C5FD)", "linear-gradient(135deg, #EFF6FF, #DBEAFE)", (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7"/>
                    <line x1="21" y1="21" x2="16" y2="16"/>
                  </svg>
                )],
                ["Wij rijden uit", "Dichtstbijzijnde specialist krijgt direct opdracht. Gemiddeld ≤ 24 uur ter plaatse.", "#E85D04", "linear-gradient(135deg, #FFEDD5, #FED7AA)", "linear-gradient(135deg, #FFF7ED, #FFEDD5)", (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C44D00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="7" width="14" height="10" rx="1"/>
                    <path d="M15 11h4l3 3v3h-7"/>
                    <circle cx="6" cy="19" r="2"/>
                    <circle cx="18" cy="19" r="2"/>
                  </svg>
                )],
                ["Aanpakken", "Vaste prijs vooraf, direct uitvoeren. Klaar wanneer u tevreden bent.", "#7C3AED", "linear-gradient(135deg, #EDE9FE, #C4B5FD)", "linear-gradient(135deg, #F5F3FF, #EDE9FE)", (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6D28D9" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                )]].
                map(([t, d, accent, bg, wrap, icon], i) =>
                <li key={t} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    <span style={{
                    width: 56, height: 56, flexShrink: 0,
                    background: wrap,
                    borderRadius: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 4px 14px ${accent}28`,
                    position: 'relative',
                  }}>
                      <span style={{
                        width: 38, height: 38,
                        background: bg,
                        borderRadius: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 2px 8px ${accent}33`,
                      }}>{icon}</span>
                    </span>
                    <div>
                      <strong style={{ color: 'var(--navy)', fontSize: '1rem' }}>{t}</strong>
                      <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '.93rem' }}>{d}</p>
                    </div>
                  </li>
                )}
              </ol>
            </div>
            <div className="card" style={{ background: 'var(--navy)', color: 'white', padding: 36, borderRadius: 'var(--radius-lg)' }}>
              <div style={{ fontSize: '.78rem', color: 'var(--orange)', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}>
                ● Live spoedlijn
              </div>
              <div style={{ fontSize: '2.4rem', fontWeight: 900, margin: '14px 0 8px' }}>085 500 50 50</div>
              <p style={{ color: 'rgba(255,255,255,.7)', margin: '0 0 24px', fontSize: '.95rem' }}>
                24 uur per dag, 7 dagen per week. Vakman aan de lijn, geen wachtmuziek.
              </p>
              <div style={{ display: 'grid', gap: 12 }}>
                {[["Ma, Vr 07:00-22:00", "Normale tarieven"], ["Za, Zo 09:00-20:00", "Normale tarieven"], ["Nacht 22:00-07:00", "Toeslag tot 100%"], ["Reactietijd", "≤ 24 uur"]].map(([k, v]) =>
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
                    <span style={{ color: 'rgba(255,255,255,.65)', fontSize: '.88rem' }}>{k}</span>
                    <strong style={{ fontSize: '.88rem' }}>{v}</strong>
                  </div>
                )}
              </div>
              <a href="tel:0855005050" className="btn-meer" style={{ marginTop: 24, width: '100%', justifyContent: 'center' }}>
                <IconPhone width="16" height="16" /> Bel direct
              </a>
            </div>
          </div>
          <style>{`@media (max-width:880px){ .sp-info{ grid-template-columns: 1fr !important; } }`}</style>
        </div>
      </section>

      {/* ─── Waarom direct bellen, content voor SEO + intent-match ───── */}
      <section className="section">
        <div className="container" style={{ maxWidth: 860 }}>
          <div className="sh">
            <div className="section-tag">Waarom direct?</div>
            <h2>Waarom je bij ongedierte <em>snel moet zijn</em></h2>
          </div>
          <div style={{ display: 'grid', gap: 22, color: 'var(--text)', fontSize: '1.02rem', lineHeight: 1.7 }}>
            <p>
              Bij sommige soorten ongedierte heeft wachten een directe prijs. Een
              wespennest dat vandaag 50 wespen telt, kan binnen twee weken
              uitgegroeid zijn tot 2.000. Ratten in huis vermenigvuldigen zich
              met 8 jongen per nest, om de zes weken. En bedwantsen leggen tussen
              de 200 en 500 eitjes per vrouwtje gedurende hun leven. <strong>Snel
              ingrijpen scheelt niet alleen een grotere plaag, het scheelt ook
              gezondheidsrisico's, materiële schade en horeca-sluitingen.</strong>
            </p>
            <p>
              ABC Ongediertebestrijding heeft een speciale spoeddienst die 24 uur
              per dag, 7 dagen per week bereikbaar is. Geen wachtmuziek, een
              vakman pakt direct op, beoordeelt jouw situatie en zet
              binnen 24 uur (vaak veel sneller) een gecertificeerde specialist op
              jouw locatie. De spoedlijn is bedoeld voor situaties waarin een
              normale planning van 2-3 dagen onverantwoord is: gezondheidsrisico,
              gevaar voor kinderen, lopende NVWA-inspectie of zichtbare schade
              aan eigendom.
            </p>
            <p>
              Onze bestrijders zijn dagelijks actief in heel <strong>Zuid-Holland,
              Noord-Holland en Utrecht</strong>. Vanaf de eerste melding tot
              aankomst op locatie houden wij telefonisch contact, zodat je weet
              hoe laat de bestrijder arriveert. Bij aankomst krijg je
              een vaste prijsindicatie, geen verrassingen achteraf.
            </p>
          </div>
        </div>
      </section>

      {/* ─── Prijs + responstijd transparantie ─ premium redesign ─── */}
      <section className="section section-bg">
        <div className="container">
          <div className="sh">
            <div className="section-tag">Tarieven spoed</div>
            <h2>Eerlijke prijzen, <em>geen verborgen kosten</em></h2>
            <p style={{ color: 'var(--text-muted)', maxWidth: '60ch', margin: '8px auto 0' }}>
              Vaste startprijzen, definitieve prijs vooraf bij telefonische intake.
            </p>
          </div>
          {/* Info-grid in editorial card-stijl. Eerste 3 cards = prijzen,
              laatste 3 = reactietijden. Visueel onderscheiden door een
              kleine icon-strip bovenaan + label-stijl. */}
          <div className="info-card-grid" style={{ marginTop: 32 }}>
            {[
              { type: 'price', icon: '🐝', label: 'Wespen-spoed (overdag)', price: 'Prijs telefonisch', sub: 'Inclusief nest-aanpak' },
              { type: 'price', icon: '🌙', label: 'Wespen-spoed (avond/weekend)', price: 'Prijs telefonisch', sub: 'Geen extra toeslag tot 22:00' },
              { type: 'price', icon: '🐀', label: 'Ratten/muizen-spoed', price: 'Prijs telefonisch', sub: 'Inclusief eerste behandeling' },
              { type: 'time',  icon: '⏱', label: 'Nacht-spoed (22:00-07:00)', price: 'Spoedtoeslag', sub: 'Afhankelijk van locatie bestrijder' },
              { type: 'time',  icon: '📍', label: 'Reactietijd gemiddeld', price: '≤ 24 uur', sub: 'Hele werkgebied ZH/NH/UT' },
              { type: 'time',  icon: '⚡', label: 'Reactietijd Den Haag / Rotterdam', price: '≤ 6 uur', sub: 'Spoed in eigen regio' },
            ].map((c) => (
              <div key={c.label} className={`info-card info-card--${c.type}`}>
                <div className="info-card-icon" aria-hidden="true">{c.icon}</div>
                <div className="info-card-body">
                  <div className="info-card-label">{c.label}</div>
                  <div className="info-card-value">{c.price}</div>
                  <div className="info-card-sub">{c.sub}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '.92rem', lineHeight: 1.6, textAlign: 'center', maxWidth: '64ch', margin: '32px auto 0' }}>
            Alle prijzen inclusief BTW, voorrijkosten en eerste behandeling.
            Bij telefonische intake krijg je een definitieve prijs op basis van
            jouw situatie, pas na akkoord starten wij met de behandeling.
          </p>
        </div>
      </section>

      {/* ─── FAQ-sectie ─ zelfde collapsible stijl als stad-pagina's ── */}
      <section className="section faq-section">
        <div className="container" style={{ maxWidth: 860 }}>
          <div className="sh">
            <div className="section-tag">Veelgestelde vragen</div>
            <h2>FAQ over <em>spoeddienst</em></h2>
          </div>
          <div className="faq-list">
            {SPOED_FAQS.map((f, i) => (
              <div key={i} className={"faq-item" + (openFaq === i ? " open" : "")}>
                <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? -1 : i)} aria-expanded={openFaq === i}>
                  <span>{f.q}</span>
                  <span className="faq-icon" aria-hidden="true">+</span>
                </button>
                <div className="faq-answer"><p>{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>);

}

// ──────────────────────────────────────────────────────────────────────

export function BlogPage() {
  const [pc, setPc] = useState("");
  const formatPc = (raw) => { let v = raw.replace(/\s/g, '').toUpperCase(); if (v.length > 4) v = v.slice(0, 4) + ' ' + v.slice(4, 6); return v; };
  const searchPostcode = () => {
    const r = pc.trim().replace(/\s/g, '');
    if (r.length < 4) { alert('Vul een geldige postcode in'); return; }
    const city = cityForPostcode(r);
    navigate(city ? `/werkgebied/${city}?pc=${encodeURIComponent(r)}` : `/offerte?pc=${encodeURIComponent(r)}`);
  };
  const [hf, setHf] = useState({ naam: "", telefoon: "", postcode: "", ongedierte: "" });
  const [hfSent, setHfSent] = useState(false);
  const updHf = k => e => setHf(f => ({ ...f, [k]: e.target.value }));
  const [openArticle, setOpenArticle] = useState(null);

  useEffect(() => {
    if (!openArticle) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setOpenArticle(null); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [openArticle]);

  const articles = [
  { slug: "muizen-herkennen", t: "Muizen in huis: 7 signalen + wat je vandaag nog moet doen", d: "Knaagsporen, keuteltjes en geritsel, herken een muizenplaag in 5 minuten en weet wanneer bellen écht moet.", cat: "Muizen", date: "12 mei 2026", img: "/assets/blog-muizen.jpg",
    minutes: 8,
    body: `## Eén muis in huis = nooit één muis

Tegen de tijd dat u keuteltjes vindt of geritsel hoort, is er meestal al een nest actief. Een vrouwtjesmuis kan **tot 80 nakomelingen per jaar** krijgen, en die nakomelingen krijgen op hun beurt jongen na 6 weken. Wachten verergert het probleem exponentieel.

Wij krijgen elk najaar tientallen telefoontjes van mensen die "één muisje" zagen en drie weken later opeens met zes tegelijk te maken hebben. Het verschil tussen vroeg ingrijpen en wachten is fors: een eenvoudige bron-aanpak versus weken aan uitbreidende overlast.

## De 7 signalen, hoe vroeger u ze herkent, hoe goedkoper

### 1. Keuteltjes, het duidelijkste signaal

Muizenkeutels zijn **3-6 mm lang**, donkerbruin tot zwart, en hebben de vorm van rijstkorrels met spitse punten. U vindt ze typisch op deze plekken:

- Langs plinten en in hoeken
- In keukenlades, vooral achterin
- Op planken in de meterkast
- Onder of achter de koelkast
- In zolderkasten en de werkkast

**Vers vs oud:** verse keuteltjes glimmen licht, oude zijn dof en breken makkelijk. Vers = actief probleem. Oud = mogelijk eerder probleem, maar tóch laten inspecteren want muizen keren terug naar bekende locaties.

### 2. Knaagsporen op kabels, verpakkingen, plinten

Muizen knagen voortdurend om hun tanden in te slijpen, die groeien hun hele leven. Typische sporen:

- Tweeparallelle groeven op kabels (elektriciteit, internet), **brandgevaar**
- Onregelmatige randen op voedselverpakkingen (vooral droog voer, brood, chocolade)
- Houtafval bij plinten of houten kasten
- Schade aan isolatiematerialen op zolder

⚠️ **Brandgevaar:** ongeveer 20% van onverklaarbare woningbranden in Nederland heeft een knaag-oorzaak. Vinden van geknaagde stroomkabels = direct uitschakelen + bestrijder + ongediertebestrijder.

### 3. Geritsel, vooral 's nachts

Muizen zijn nachtdieren. Tussen 22:00 en 04:00 hoort u meestal:

- Krabbelende geluiden achter wanden
- Geritsel boven het plafond (zolder/spouw)
- Gepiep, meestal jonge muizen of communicatie tussen volwassenen
- Schrapende geluiden uit luchtventilatie of dakgoten

Test: leg op een verdacht moment een blanco wit A4-tje voor de waarschijnlijke route en strooi er een dun laagje meel over. 's Ochtends ziet u pootafdrukken als ze actief waren.

### 4. Muffe, ammoniak-achtige geur

Muizen-urine ruikt scherp en zoet tegelijk, vaak omschreven als "ranzige boter" of "ammoniak met poeder". Vooral merkbaar in:

- Krappe ruimtes (kasten, kruipruimte)
- Achter de koelkast of vaatwasser
- In de meterkast

Een aanhoudende muffe geur die niet weggaat na schoonmaken = bijna zeker een actief nest. Hond of kat reageert hier het eerst op door obsessief één plek te besnuffelen.

### 5. Vettige veegsporen langs plinten

Muizen volgen vaste looproutes. Hun vacht zit vol stof + huidolie, waardoor er **vettige bruine vegen** ontstaan langs de wanden waar ze voortdurend langs schuren. Deze sporen zijn:

- Donkerder dan de muur eromheen
- Vaak op ~3-4 cm hoogte (heuphoogte van muis)
- Continue lijnen, niet vlekken

In oudere huizen met witte plinten is dit een van de duidelijkste signalen.

### 6. Reactie van huisdieren

Honden en katten merken muizen 1-2 weken eerder op dan mensen. Tekenen:

- Kat staart obsessief naar één plek in de wand
- Hond gromt of blaft naar gesloten kasten of meterkast
- Huisdier gedraagt zich onrustig in een specifieke kamer

Negeer dit niet, uw huisdier vertelt u letterlijk waar het nest zit.

### 7. Muizen zelf zien

Het laatste signaal. Als u overdag een muis ziet, is de populatie waarschijnlijk **al groot genoeg dat het nest overbevolkt is**. Muizen vermijden licht en mensen, dat ze toch zichtbaar zijn betekent dat de veilige plekken vol zijn.

## Wanneer is het tijd om te bellen?

**Direct bellen:**
- 2 of meer van bovenstaande signalen tegelijk
- Knaagschade aan elektrische bekabeling
- Zwangere vrouw of pasgeboren baby in huis (gezondheidsrisico)
- Horeca / voedsel-gerelateerd bedrijf (NVWA-meldplicht)

**Binnen een week bellen:**
- 1 signaal maar herhaaldelijk dezelfde plek
- Sporen in de keuken of badkamer
- U twijfelt over de oorsprong van een geur of geluid

## Wat doet ABC anders dan zelf bestrijden?

Veel mensen proberen eerst zelf met klemvallen of vergiftigde lokstations uit de bouwmarkt. Dat werkt zelden duurzaam. Hier waarom:

- **Bron blijft open**, vallen vangen muizen maar dichten niet de ingang. Volgende generatie komt zo terug binnen.
- **Vergif is gevaarlijk**, voor huisdieren, kinderen, én andere dieren die de muis daarna eten (uilen, katten).
- **Geen monitoring**, zonder lokstations met telkaart weet u niet of de plaag echt over is.

Onze aanpak bij muizenbestrijding:

1. **Vakkundige inspectie** door een gecertificeerde specialist, wij vinden niet alleen muizen, maar ook de ingangspunten (kruipruimte, leidingen, dakgoten).
2. **CTGB-gecertificeerde middelen**, veilig voor mens en huisdier wanneer correct toegepast, en getest op effectiviteit.
3. **Proofing**, wij dichten de ingangspunten af zodat het probleem niet terugkomt.
4. **Nacontrole na 2 weken**, wij komen terug om het resultaat te checken en adviseren over preventie.

## Wat kost muizenbestrijding?

De prijs hangt af van de omvang van de plaag, het aantal toegangspunten en de benodigde proofing. Bij een eenvoudige situatie (1 ingangspunt, kleine ruimte) is de aanpak veel beperkter dan bij een uitgebreide plaag in kruipruimte of meerdere verdiepingen.

Wij geven aan de telefoon al een **eerlijke prijsindicatie** en bespreken de definitieve prijs altijd vóór de afspraak. Geen verrassingen achteraf. De inspectie zelf is altijd gratis.

## Direct contact

Twee of meer signalen herkend? Wacht niet tot het uit de hand loopt.

📞 **Bel direct: 085 500 50 50** (7 dagen per week, spoed 24/7)
📧 **E-mail:** stuur ons foto's van wat u vond via info@abcongedierte.nl, dan adviseren wij direct
📝 **Gratis offerte aanvragen:** vul uw postcode in en wij sturen de dichtstbijzijnde specialist`
  },
  { slug: "wespennest-zelf", t: "Wespennest verwijderen: wel of niet zelf doen?", d: "De échte risico's en wanneer een vakman bellen.", cat: "Wespen", date: "8 mei 2026", img: "/assets/blog-wespen.jpg",
    body: `Korte antwoord: niet zelf doen.\n\nWespen verdedigen hun nest agressief. Een kolonie kan in augustus tot 10.000 wespen tellen, een aanval levert honderden steken op binnen seconden, ook bij mensen zonder allergie.\n\n**Waarom een professional?**\n• Beschermende kleding en gespecialiseerde apparatuur.\n• CTGB-gecertificeerd wespenpoeder (niet in winkel verkrijgbaar).\n• Telescoopapparatuur voor hoge nesten.\n• Seizoensgarantie, komt het terug, behandelen wij gratis opnieuw.\n\nDIY-trucs zoals nest verbranden of bedekken met een vuilniszak veroorzaken jaarlijks ziekenhuisopnames. Bel een professional, prijs altijd telefonisch vooraf.`
  },
  { slug: "ratten-kosten", t: "Wat kost rattenbestrijding in Zuid-Holland?", d: "Een eerlijke prijsvergelijking en wat een offerte beïnvloedt.", cat: "Ratten", date: "1 mei 2026", img: "/assets/blog-ratten.jpg",
    body: `De prijs voor rattenbestrijding hangt sterk af van de situatie. Wij geven altijd telefonisch een eerlijke prijsindicatie vóór de afspraak, zodat u nooit voor verrassingen komt te staan.\n\n**Wat bepaalt de prijs?**\n• Locatie (binnen vs. buiten vs. beide)\n• Omvang van de plaag\n• Toegankelijkheid (kruipruimte, riool, dakgoten)\n• Benodigde proofing (dichten van ingangen)\n• Aantal bezoeken\n\n**Wat is er bij de prijs inbegrepen?**\n• Inspectie ter plaatse\n• Plaatsen van professionele vangkasten\n• Proofing op gevonden ingangspunten\n• Nacontrole na 2 weken\n• Vrijblijvende offerte vooraf\n\nLet op: bij ernstige rioolaansluitingen kan een gemeente-aanvraag nodig zijn, wij begeleiden dat traject.`
  },
  { slug: "bedwantsen-reis", t: "Bedwantsen na een reis: zo voorkom je het", d: "Hotel-trucs en wat te doen na thuiskomst.", cat: "Bedwantsen", date: "22 april 2026", img: "/assets/blog-bedwantsen.jpg",
    body: `Bedwantsen reizen mee in koffers, kleding en zelfs laptoptassen. Een hotel waar ze actief zijn is moeilijk visueel te detecteren.\n\n**In het hotel:**\n• Plaats uw koffer NIET op het bed, gebruik de koffer-standaard of badkamer.\n• Controleer de naden van het matras, koplamp en achterkant van het hoofdeind.\n• Zoek naar kleine bruine vlekjes (uitwerpselen) of doorzichtige velletjes.\n\n**Na thuiskomst:**\n• Pak uit in de gang/garage, niet in de slaapkamer.\n• Was alle kleding op minimaal 60°C.\n• Stop koffer 4 dagen in de vriezer of 1 dag in de zon (>50°C).\n• Bij twijfel: bel ons binnen 24 uur, hoe eerder, hoe goedkoper de behandeling.`
  },
  { slug: "haccp-horeca", t: "HACCP & ongedierte: wat verlangt NVWA?", d: "Een complete checklist voor restauranthouders.", cat: "Horeca", date: "15 april 2026", img: "/assets/blog-horeca.jpg",
    body: `De NVWA controleert horeca regelmatig op ongediertepreventie. Een tekortkoming kan leiden tot waarschuwing, boete of zelfs sluiting.\n\n**Wat verwacht NVWA?**\n• Schriftelijk plan: wie controleert wanneer wat?\n• Logboek van waarnemingen en behandelingen (dit houdt u zelf bij of uw HACCP-adviseur).\n• Contract met erkende bestrijder (KPMB-gecertificeerde partners).\n• Visuele inspectie keuken, opslag, kruipruimte, dakgoten, minimaal 1× per kwartaal.\n• Lokstations met monitoringkaart, niet in voedselzones.\n\n**Wat doen wij voor u?**\n• Vaste kwartaalcontroles met vakkundige aanpak.\n• Mondelinge terugkoppeling na elk bezoek, zodat u uw eigen administratie bij kunt houden.\n• Spoeddienst bij meldingen, geen NVWA-sluiting door snelle escalatie.\n• Vaste prijs vooraf, geen verrassingen.\n\nVraag een vrijblijvende offerte aan voor een HACCP-conform contract.`
  },
  { slug: "kakkerlakken-bron", t: "Kakkerlakken: waar komen ze écht vandaan?", d: "De bron vinden is de helft van het werk.", cat: "Kakkerlakken", date: "3 april 2026", img: "/assets/blog-kakkerlakken.jpg",
    body: `Een kakkerlak in zijn eentje bestaat niet. Als u er één ziet, zijn er waarschijnlijk al tientallen verstopt.\n\n**Waar komen ze meestal vandaan?**\n• Via leidingschachten, vooral in flatgebouwen.\n• Met dozen, kleding, tweedehands meubels.\n• Buurtprobleem, uw flatgenoot kan de bron zijn.\n• Levering uit restaurants/winkels met eigen plaag.\n\n**Hoe vinden wij de bron?**\n• Lokstations met monitoringkaart, zo zien we waar ze komen en gaan.\n• Inspectie van leidingdoorvoeringen, achter koelkast, in meterkast.\n• UV-lamp 's nachts, kakkerlakken fluoresceren licht onder UV.\n\nBronnaanpak is essentieel. Sprayen helpt 2 weken, daarna komen ze terug. Gel-aas + monitoring + bron dicht = definitief weg.`
  }];

  // SEO Fix 5 (2026-05-24): Blog + BlogPosting schema voor rich snippets.
  // Hierdoor verschijnen artikelen met datum, lengte en categorie in
  // Google's "Top Stories" en News-resultaten. Zonder dit zien blog-posts
  // er als gewone tekst-links uit.
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "ABC Ongediertebestrijding Kennisbank",
    "description": "Tips, gidsen en kennis over ongediertebestrijding in Nederland.",
    "url": "https://www.abcongedierte.nl/blog/",
    "publisher": { "@id": "https://www.abcongedierte.nl/#organization" },
    "blogPost": articles.map(a => ({
      "@type": "BlogPosting",
      "headline": a.t,
      "description": a.d,
      "datePublished": a.date,
      "url": `https://www.abcongedierte.nl/blog/#${a.slug}`,
      "image": `https://www.abcongedierte.nl${a.img}`,
      "articleSection": a.cat,
      "wordCount": Math.round((a.body || '').length / 5),
      "timeRequired": `PT${a.minutes || 5}M`,
      "author": { "@id": "https://www.abcongedierte.nl/#organization" },
      "publisher": { "@id": "https://www.abcongedierte.nl/#organization" },
    })),
  };

  useSEO({
    title: "Blog & Kennisbank, Ongediertebestrijding",
    description: "Tips, gidsen en kennis over ongediertebestrijding. Hoe herken je muizen? Wespennest verwijderen? Wat kost rattenbestrijding? Alle antwoorden op één plek.",
    path: "/blog",
    jsonLd: [
      buildBreadcrumb([{ name: "Home", url: "/" }, { name: "Blog", url: "/blog" }]),
      blogSchema,
    ],
  });

  return (
    <div className="page-anim">
      <section className="hero">
        <div className="container">
          <div className="hero-inner">
            <div>
              <div className="breadcrumb" style={{ marginBottom: 18 }}>
                <a href={url.home()}>Home</a><span className="sep">/</span>
                <span style={{ color: 'rgba(255,255,255,.9)' }}>Blog &amp; Kennisbank</span>
              </div>
              <div className="hero-badge">⚡ Tips van de vakman</div>
              <h1>Blog &amp; <em>Kennisbank</em></h1>
              <p className="hero-sub">Tips van de vakman. Hoe herken je een plaag, wat kost het, en wat kun je zelf doen?</p>
              <HeroPostcodeBar pc={pc} setPc={setPc} formatPc={formatPc} searchPostcode={searchPostcode} />
              <div className="hero-rating-mobile"><RatingBar /></div>
              <div className="hero-trust">
                <div className="tit"><span className="tit-mark">✓</span> Spoeddienst 24/7</div>
                <div className="tit"><span className="tit-mark">✓</span> Snel ter plaatse</div>
                <div className="tit"><span className="tit-mark">✓</span> Vaste prijs vooraf</div>
                <div className="tit"><span className="tit-mark">✓</span> Particulier &amp; zakelijk</div>
              </div>
              <div className="hero-rating-desktop"><RatingBar /></div>
            </div>
            <RotterdamForm variant="hero" />
          </div>
        </div>
      </section>

      <section className="section section-bg">
        <div className="container">
          <div className="sh">
            <div className="section-tag">Kennisbank</div>
            <h2>Onze laatste <em>artikelen</em></h2>
            <p>Praktische tips, signalen om op te letten en eerlijke prijsinformatie.</p>
          </div>
          <div className="blog-grid">
            {articles.map((a) =>
            <article key={a.slug} className="blog-card" onClick={() => setOpenArticle(a)}>
                <div className="bc-media">
                  {a.img ? (
                    <picture>
                      <source srcSet={a.img.replace(/\.(jpg|png)$/, '.webp')} type="image/webp" />
                      <img
                        src={a.img}
                        alt={a.t}
                        width="600"
                        height="340"
                        loading="lazy"
                        decoding="async"
                        className="bc-img"
                      />
                    </picture>
                  ) : (
                    <div className="bc-img-fallback">{a.cat}</div>
                  )}
                  <span className="bc-cat">{a.cat}</span>
                </div>
                <div className="bc-body">
                  <div className="bc-date">{a.date} &middot; 4 min leestijd</div>
                  <h3 className="bc-title">{a.t}</h3>
                  <p className="bc-desc">{a.d}</p>
                  <span className="bc-link">Lees artikel <span className="bc-arrow">→</span></span>
                </div>
              </article>
            )}
          </div>
        </div>
      </section>

      {/* 2026-05-29 — blog-index uitgebreid van ~590 naar ~1100 woorden
          met categorie-uitleg, seizoens-tips, en algemene preventie-FAQs.
          Geeft ranking-kans op "tips ongedierte voorkomen" en variants. */}
      <section className="section">
        <div className="container" style={{ maxWidth: 920 }}>
          <div className="sh">
            <div className="section-tag">Categorieën</div>
            <h2 style={{ fontSize: 'clamp(1.6rem,2.8vw,2.2rem)', color: 'var(--navy)', margin: '10px 0 18px', lineHeight: 1.2 }}>
              Waarover wij <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>schrijven</em>
            </h2>
            <p style={{ color: 'var(--text)', lineHeight: 1.75, marginBottom: 22, maxWidth: 720 }}>
              Onze kennisbank bundelt jarenlange ervaring van onze bestrijders. Wij delen praktische tips uit het veld: hoe herken je een plaag voordat hij erg groot wordt, wat kun je zelf doen voordat je belt, en wanneer is het écht tijd voor professionele hulp. Geen theoretische verhalen, alleen wat werkt.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--navy)', margin: '0 0 8px' }}>Herkenning en signalen</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                Hoe weet je of die geluiden 's nachts muizen of vogels zijn? Wat is het verschil tussen wespen en bijen? Welke keuteltjes horen bij welke plaag? Vroege herkenning bespaart geld en frustratie.
              </p>
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--navy)', margin: '0 0 8px' }}>Preventie thuis</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                Welke kieren laten muizen binnen? Hoe voorkom je bedwantsen na een vakantie? Wat kun je in de tuin doen tegen wespen? Praktische maatregelen die u zelf kunt nemen.
              </p>
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--navy)', margin: '0 0 8px' }}>Seizoensoverzichten</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                Lente: mollen actief in tuinen. Zomer: wespennesten op piek. Herfst: muizen trekken vanuit polder naar huis. Winter: ratten zoeken warmte. Wat speelt wanneer.
              </p>
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--navy)', margin: '0 0 8px' }}>Bedrijven en horeca</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                NVWA-controle, HACCP-eisen, preventie in restaurants en magazijnen. Wat moet je weten als bedrijf? Welke risico's loop je bij niet-handelen? Tips voor zakelijke beheerders.
              </p>
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--navy)', margin: '0 0 8px' }}>Wat-werkt-wat-niet</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                Werken ultrasone verjagers tegen mollen? Helpen mottenballen tegen muizen? Doet pepermuntolie wat tegen ratten? Wij ontkrachten mythen op basis van praktijkervaring.
              </p>
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--navy)', margin: '0 0 8px' }}>Wettelijk en veiligheid</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                Welke middelen zijn nog toegestaan? Wat mag je zelf en wat niet? Allergieën, kinderen en huisdieren tijdens behandeling. Wat de wet voorschrijft.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-bg">
        <div className="container" style={{ maxWidth: 880 }}>
          <div className="sh">
            <div className="section-tag">Veelgestelde vragen</div>
            <h2 style={{ fontSize: 'clamp(1.6rem,2.8vw,2.2rem)', color: 'var(--navy)', margin: '10px 0 20px', lineHeight: 1.2 }}>
              Algemene vragen over <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>ongediertebestrijding</em>
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { q: "Wanneer moet ik een professional inschakelen?", a: "Bij elke plaag die u niet binnen een week zelf onder controle krijgt, bij allergie of kleine kinderen, bij commerciële panden (horeca, voedselverwerking), of als de plaag terugkomt na uw eerste poging. Hoe vroeger u professionele hulp inschakelt, hoe goedkoper en sneller de aanpak meestal is." },
              { q: "Wat doet ABC anders dan andere bestrijders?", a: "Wij werken altijd eerst aan de oorzaak, niet alleen aan de symptomen. Een nest weghalen lost het probleem voor één seizoen op, maar als de invlieg-route niet wordt gedicht komt het terug. Onze bestrijders nemen de tijd voor inspectie zodat de behandeling structureel werkt." },
              { q: "Hoeveel kost ongediertebestrijding gemiddeld?", a: "De prijs hangt af van de plaag, de omvang en de locatie. Voor een eenvoudige situatie (één wespennest, beginnende muizenoverlast) zit u meestal tussen 100 en 200 euro. Voor uitgebreide aanpak (rattenproblematiek, bedwantsen-hittebehandeling) loopt het op naar enkele honderden euro's. Wij geven altijd een prijsindicatie aan de telefoon vóór de afspraak." },
              { q: "Werkt zelf bestrijden of moet ik altijd een specialist bellen?", a: "Voor een enkele muis of de eerste paar wespen kunt u thuis veel zelf doen: lokmiddelen uit de bouwmarkt, gevelopeningen dichten, val uitzetten. Werken die niet binnen een week? Of breidt de plaag uit? Dan is professionele hulp meestal goedkoper dan blijven doorklungelen. Voor ratten, bedwantsen en kakkerlakken raden wij altijd direct aan een specialist te bellen." },
              { q: "Hoe weet ik dat een ongediertebestrijder betrouwbaar is?", a: "Let op CTGB-certificering van de gebruikte middelen, KPMB of IPM-keurmerk, transparantie over prijs (vooraf duidelijk), reviews van vorige klanten, en een vast adres en KvK-nummer. Echte bestrijders rekenen geen voorrijkosten apart van hun behandelprijs." },
              { q: "Hoe vaak komen jullie blogartikelen toevoegen?", a: "Wij voegen regelmatig nieuwe artikelen toe op basis van wat onze bestrijders in de praktijk zien. Voor seizoensgebonden onderwerpen (wespen in zomer, muizen in herfst) publiceren wij meestal vooraf zodat u op tijd preventief kunt handelen." },
            ].map((faq, i) => (
              <details key={i} style={{ background: 'white', borderRadius: 12, border: '1px solid #E5E7EB', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: '1rem', color: 'var(--navy)', listStyle: 'none' }}>
                  {faq.q}
                </summary>
                <p style={{ marginTop: 12, color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {openArticle && (
        <div
          onClick={() => setOpenArticle(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9000,
            background: 'rgba(15,30,58,0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px 16px',
          }}
          aria-modal="true" role="dialog"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 16,
              maxWidth: 560, width: '100%', position: 'relative',
              boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
              overflow: 'hidden',
              maxHeight: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column',
            }}
          >
            <button
              onClick={() => setOpenArticle(null)}
              aria-label="Sluiten"
              style={{
                position: 'absolute', top: 10, right: 10, zIndex: 2,
                width: 30, height: 30, borderRadius: '50%',
                background: 'rgba(255,255,255,0.95)', border: 'none', cursor: 'pointer',
                fontSize: 16, fontWeight: 700, color: 'var(--navy)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)', lineHeight: 1,
              }}
            >×</button>
            {openArticle.img ? (
              <picture>
                <source srcSet={openArticle.img.replace(/\.(jpg|png)$/, '.webp')} type="image/webp" />
                <img src={openArticle.img} alt={openArticle.t}
                  width="800" height="200"
                  loading="lazy" decoding="async"
                  style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
              </picture>
            ) : (
              <div style={{
                height: 140,
                background: 'linear-gradient(135deg,var(--orange) 0%,var(--orange-dark) 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: '1.4rem', fontWeight: 900,
              }}>{openArticle.cat}</div>
            )}
            <div style={{ padding: '20px 24px 24px', overflowY: 'auto', flex: 1 }}>
              <div style={{ fontSize: '.72rem', color: 'var(--orange)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                {openArticle.cat} · {openArticle.date}
              </div>
              <h2 style={{ margin: '0 0 14px', color: 'var(--navy)', fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.25 }}>
                {openArticle.t}
              </h2>
              {openArticle.body ? (
                <div className="blog-body" style={{ color: 'var(--text)', lineHeight: 1.65, fontSize: '.95rem' }}>
                  {openArticle.body.split(/\n\n+/).map((para, i) => {
                    // Bullets block
                    if (para.split('\n').every(l => l.trim().startsWith('•'))) {
                      return (
                        <ul key={i} style={{ paddingLeft: 18, margin: '0 0 14px', listStyle: 'none' }}>
                          {para.split('\n').filter(Boolean).map((line, j) => {
                            const txt = line.replace(/^•\s*/, '');
                            return (
                              <li key={j} style={{ marginBottom: 6, paddingLeft: 14, position: 'relative' }}>
                                <span style={{ position: 'absolute', left: 0, color: 'var(--orange)', fontWeight: 800 }}>•</span>
                                <span dangerouslySetInnerHTML={{ __html: txt.replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--navy)">$1</strong>') }} />
                              </li>
                            );
                          })}
                        </ul>
                      );
                    }
                    // Paragraph (with **bold** support)
                    const html = para.replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--navy)">$1</strong>');
                    return <p key={i} style={{ margin: '0 0 14px' }} dangerouslySetInnerHTML={{ __html: html }} />;
                  })}
                </div>
              ) : (
                <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Volledig artikel wordt binnenkort gepubliceerd.
                </p>
              )}
              <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px solid var(--border)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <a href="tel:0855005050" className="btn-meer" style={{ fontSize: '.88rem', padding: '10px 16px' }}>📞 085 500 50 50</a>
                <a href={url.offerte()} className="btn-meer" style={{ fontSize: '.88rem', padding: '10px 16px', background: 'transparent', color: 'var(--orange)', border: '2px solid var(--orange)' }}>Offerte aanvragen</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>);

}

// ──────────────────────────────────────────────────────────────────────

export function ReviewsPage() {
  useSEO({
    title: "Reviews & klantbeoordelingen",
    description: `${String(REVIEWS.rating).replace('.', ',')}/5 sterren op basis van ${REVIEWS.total} Google reviews. Lees wat klanten in Rotterdam, Den Haag, Leiden en elders zeggen over ABC Ongediertebestrijding.`,
    path: "/reviews",
    jsonLd: [
      buildBreadcrumb([{ name: "Home", url: "/" }, { name: "Reviews", url: "/reviews" }]),
      // AggregateRating schema → laat Google sterren tonen in SERP snippet.
      // Verbonden aan de Organization-entity (gedefinieerd in index.html).
      {
        "@context": "https://schema.org",
        "@type": "AggregateRating",
        "itemReviewed": { "@id": "https://www.abcongedierte.nl/#organization" },
        "ratingValue": String(REVIEWS.rating),
        "reviewCount": String(REVIEWS.total),
        "bestRating": "5",
        "worstRating": "1",
      },
    ],
  });

  // reviews-pc-added
  const [pc, setPc] = useState("");
  const formatPc = (raw) => { let v = raw.replace(/\s/g, '').toUpperCase(); if (v.length > 4) v = v.slice(0,4)+' '+v.slice(4,6); return v; };
  const searchPostcode = () => { const r = pc.trim().replace(/\s/g,''); if (r.length < 4) { alert('Vul een geldige postcode in'); return; } navigate(`/offerte?pc=${encodeURIComponent(r)}`); };
  // Start altijd met de fallback-reviews (hardcoded reals uit google-reviews.js).
  // Bij client-mount fetchen we de 5 nieuwste live reviews via de Worker, en
  // mergen die voorin de lijst — gedupliceerd op naam zodat we niemand dubbel
  // tonen. Resultaat: een slider met fallback + live, automatisch up-to-date.
  const fallback = (GOOGLE_REVIEWS_FALLBACK || []);
  const [reviews, setReviews] = useState(fallback);
  useEffect(() => {
    if (!fetchGoogleReviews) return;
    fetchGoogleReviews().then(live => {
      if (!live || !live.length) return;
      const seen = new Set(live.map(r => (r.name || r.author || '').toLowerCase().trim()));
      const merged = [
        ...live,
        ...fallback.filter(f => !seen.has((f.name || f.author || '').toLowerCase().trim())),
      ];
      setReviews(merged);
    }).catch(() => {});
  }, []);

  // Live aggregaat voor de hero-badge + sub-tekst
  const agg = useGoogleAggregate();
  const ratingStr = String(agg.rating).replace('.', ',');


  return (
    <div className="page-anim">
      <section className="hero">
        <div className="container">
          <div className="hero-inner">
            <div>
              <div className="breadcrumb" style={{ marginBottom: 18 }}>
                <a href={url.home()}>Home</a><span className="sep">/</span>
                <span style={{ color: 'rgba(255,255,255,.9)' }}>Reviews</span>
              </div>
              <div className="hero-badge">⭐ {ratingStr} op {agg.total} Google reviews</div>
              <h1>Wat klanten <em>over ons zeggen</em></h1>
              <p className="hero-sub">
                {ratingStr}/5 sterren op basis van {agg.total} Google reviews. Hieronder een greep uit wat klanten teruggaven.
              </p>
              <HeroPostcodeAndTrust pc={pc} setPc={setPc} formatPc={formatPc} searchPostcode={searchPostcode} />
              <div className="hero-rating-desktop"><RatingBar /></div>
            </div>
            <RotterdamForm variant="hero" />
          </div>
        </div>
      </section>

      <ReviewsSlider reviews={reviews} />

      {/* ─── Trust-signalen + uitleg over reviews ──────────────────────── */}
      <section className="section">
        <div className="container" style={{ maxWidth: 920 }}>
          <div className="sh">
            <div className="section-tag">Wat klanten waarderen</div>
            <h2>Drie dingen komen <em>steeds terug</em> in onze reviews</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 22, marginTop: 20 }}>
            {[
              { icon: "⚡", title: "Snelheid", desc: "Bijna elke positieve review noemt de snelle reactietijd. Klanten waarderen dat wij binnen 24 uur ter plaatse zijn, vaak veel sneller, en dat er geen lange wachtlijsten zijn zoals bij grote ketens. Bij echte spoed (wespennest bij kinderen, ratten in keuken) rijden wij vrijwel altijd diezelfde dag uit." },
              { icon: "💬", title: "Communicatie", desc: "Klanten benoemen vaak dat zij van begin tot eind weten wat er gebeurt. De bestrijder belt vooraf, legt uit wat hij gaat doen, vertelt de prijs vooraf en geeft duidelijke nazorg-instructies. Geen vakjargon, geen vage prijzen, geen verrassingen." },
              { icon: "🎯", title: "Vakwerk en resultaat", desc: "De aanpak werkt, en dat blijft niet onopgemerkt. Veel reviews vermelden dat het probleem na onze behandeling definitief verholpen is. Vooral bij muizen, ratten en wespen krijgen wij vaak terugkoppeling dat klanten 'eindelijk weer rustig kunnen wonen of werken'." },
            ].map(c => (
              <div key={c.title} style={{ background: 'white', borderRadius: 14, padding: 24, border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>{c.icon}</div>
                <h3 style={{ color: 'var(--navy)', fontSize: '1.1rem', margin: '0 0 8px' }}>{c.title}</h3>
                <p style={{ color: 'var(--text)', fontSize: '.94rem', lineHeight: 1.65, margin: 0 }}>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Transparantie over hoe wij met reviews omgaan ─────────────── */}
      <section className="section section-bg">
        <div className="container" style={{ maxWidth: 860 }}>
          <div className="sh">
            <div className="section-tag">Onze aanpak</div>
            <h2>Hoe wij omgaan met <em>reviews</em></h2>
          </div>
          <div style={{ display: 'grid', gap: 18, color: 'var(--text)', fontSize: '1.02rem', lineHeight: 1.75 }}>
            <p>
              Wij vragen <strong>elke klant na een afgeronde behandeling om een review</strong>. Niet via verplichte e-mail-spam, maar via een persoonlijke uitnodiging, meestal van de bestrijder zelf na de behandeling, of via een kort bedankje-bericht 2 dagen later. Klanten kiezen zelf of ze een review willen achterlaten, en op welk platform (Google, eigen pagina, of geen).
            </p>
            <p>
              Wij <strong>filteren geen negatieve reviews weg</strong>. Op Google staan alle reviews die wij krijgen, positief én kritisch. Krijgen wij een 1- of 2-sterren review, dan nemen wij binnen 24 uur publiek contact op via dezelfde review (excuses, uitleg, voorstel hersteloplossing). Reviews zijn voor ons een leersignaal, niet een marketing-instrument.
            </p>
            <p>
              <strong>Reviews van vrienden of familie worden niet geaccepteerd.</strong> Wij hebben een interne richtlijn dat alleen klanten met een afgesloten factuur een review-uitnodiging krijgen. Google's eigen algoritme filtert daarnaast verdachte reviews (van accounts zonder activiteit, zelfde IP-adres, te frequente reviews etc) en wij doen daar niets tegenin.
            </p>
            <p>
              Onze huidige score van <strong>{String(REVIEWS.rating).replace('.', ',')} op {REVIEWS.total} Google reviews</strong> is daarmee een <strong>eerlijk gemiddelde</strong>, niet opgepoetst en niet gemanipuleerd. Een paar 3-sterren reviews drukken het gemiddelde naar beneden, daar zijn we niet bang voor, want het maakt het totaalbeeld geloofwaardig.
            </p>
          </div>
        </div>
      </section>

      {/* ─── Statistieken-strip onderaan ────────────────────────────── */}
      <section className="section">
        <div className="container" style={{ maxWidth: 860 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
            {[
              { n: REVIEWS.ratingStr, l: "Gemiddelde score" },
              { n: String(REVIEWS.total), l: "Google reviews" },
              { n: "94%", l: "5-sterren reviews" },
              { n: "<24u", l: "Reactietijd op review" },
            ].map(s => (
              <div key={s.l} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, padding: 22, textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--orange)', lineHeight: 1 }}>{s.n}</div>
                <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginTop: 8, fontWeight: 600 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>);

}

// ──────────────────────────────────────────────────────────────────────

export function ContactPage() {
  // FAQ-data als const → zelfde lijst voor render én FAQPage schema.
  const CONTACT_FAQS = [
    { q: "Wat is de snelste manier om jullie te bereiken?",
      a: "Bellen op 085 500 50 50. Wij nemen gemiddeld binnen 11 seconden op, een vakman aan de lijn die jouw situatie direct inschat en een vaste prijs geeft. Geen wachtmuziek." },
    { q: "Kan ik ook 's avonds of in het weekend bellen?",
      a: "Ja, de spoedlijn 085 500 50 50 is 24/7 bereikbaar. Voor planning (niet-urgent) zijn wij beschikbaar ma-vr 07:00-22:00 en za-zo 09:00-20:00. Buiten kantooruren krijgt je gesprek voorrang als het écht spoed is (allergie, kinderen, NVWA-controle)." },
    { q: "Kan ik ook een foto sturen van het probleem?",
      a: "Ja, mail een korte beschrijving + foto naar info@abcongedierte.nl of upload deze via het offerte-formulier. Wij reageren binnen 30 minuten op werkdagen met een advies en prijsindicatie. Bij spoed liever bellen, sneller en wij beoordelen direct of er een uitruk-noodzaak is." },
    { q: "Wanneer reageren jullie op een e-mail?",
      a: "Binnen 24 uur op werkdagen. Voor niet-urgente vragen werkt e-mail prima (info@abcongedierte.nl), maar voor spoed bel altijd liever, een mail loopt makkelijk onder andere mails verloren als jouw inbox vol staat." },
    { q: "Kan ik langskomen op kantoor?",
      a: "Onze hoofdvestiging staat aan de Fuchsiastraat 55 in Den Haag. Wij ontvangen daar geen klanten zonder afspraak (geen baliefunctie), al onze bestrijders werken op locatie. Voor zakelijke gesprekken (preventiecontracten, HACCP-audits) plannen wij graag een persoonlijke afspraak op jouw locatie of bij ons." },
    { q: "Wat heeft jullie nodig om snel te kunnen helpen?",
      a: "Jouw postcode + huisnummer (voor het werkgebied + rij-afstand), type plaag of beschrijving van wat je ziet, en hoe urgent het is (kinderen aanwezig, allergie, NVWA-druk). Met die info geven wij direct een prijs-bandbreedte en plannen wij de bestrijder in." },
  ];

  useSEO({
    title: "Contact",
    description: "Bel 085 500 50 50 of mail info@abcongedierte.nl. Bereikbaar 24/7 voor spoed, ma-zo 07:00-22:00 voor planning. Fuchsiastraat 55, Den Haag.",
    path: "/contact",
    jsonLd: [
      buildBreadcrumb([{ name: "Home", url: "/" }, { name: "Contact", url: "/contact" }]),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": CONTACT_FAQS.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
    ],
  });

  // contact-pc-added
  const [pc, setPc] = useState("");
  const [openFaq, setOpenFaq] = useState(0);
  const formatPc = (raw) => { let v = raw.replace(/\s/g, '').toUpperCase(); if (v.length > 4) v = v.slice(0,4)+' '+v.slice(4,6); return v; };
  const searchPostcode = () => { const r = pc.trim().replace(/\s/g,''); if (r.length < 4) { alert('Vul een geldige postcode in'); return; } navigate(`/offerte?pc=${encodeURIComponent(r)}`); };

  return (
    <div className="page-anim">
      <section className="hero">
        <div className="container">
          <div className="hero-inner">
            <div>
              <div className="breadcrumb" style={{ marginBottom: 18 }}>
                <a href={url.home()}>Home</a><span className="sep">/</span>
                <span style={{ color: 'rgba(255,255,255,.9)' }}>Contact</span>
              </div>
              <div className="hero-badge">📞 24/7 spoedlijn · ma-vr 07:00-22:00 planning</div>
              <h1>Contact &amp; <em>bereikbaarheid</em></h1>
              <p className="hero-sub">
                Liever bellen? Bel direct. Liever mailen? Wij reageren binnen 24 uur op werkdagen.
              </p>
              <HeroPostcodeAndTrust pc={pc} setPc={setPc} formatPc={formatPc} searchPostcode={searchPostcode} />
              <div className="hero-rating-desktop"><RatingBar /></div>
            </div>
            <RotterdamForm variant="hero" />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }} className="ct-info">
            <div className="card" style={{ padding: 32 }}>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--navy)', margin: '0 0 24px' }}>Bereik ons direct</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <a href="tel:0855005050" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span style={{ width: 44, height: 44, background: 'linear-gradient(135deg, #DCFCE7, #BBF7D0)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#15803D', boxShadow: '0 4px 12px rgba(34,197,94,0.25)' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.64 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l.81-.81a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 17z"/></svg>
                  </span>
                  <span><strong style={{ color: 'var(--navy)', fontSize: '1.05rem' }}>085 500 50 50</strong><br /><span style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>Spoed 24/7</span></span>
                </a>
                <a href="mailto:info@abcongedierte.nl" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span style={{ width: 44, height: 44, background: 'linear-gradient(135deg, #DBEAFE, #93C5FD)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1D4ED8', boxShadow: '0 4px 12px rgba(59,130,246,0.25)' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></svg>
                  </span>
                  <span><strong style={{ color: 'var(--navy)', fontSize: '1.05rem' }}>info@abcongedierte.nl</strong><br /><span style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>Reactie binnen 24 uur</span></span>
                </a>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <span style={{ width: 44, height: 44, background: 'linear-gradient(135deg, #FFEDD5, #FED7AA)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C44D00', flexShrink: 0, boxShadow: '0 4px 12px rgba(232,93,4,0.25)' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  </span>
                  <span><strong style={{ color: 'var(--navy)', fontSize: '1.05rem' }}>Fuchsiastraat 55</strong><br /><span style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>2565 PL Den Haag</span></span>
                </div>
              </div>
              <div style={{ marginTop: 28, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
                <h3 style={{ fontSize: '.9rem', color: 'var(--navy)', fontWeight: 700, margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '.06em' }}>Bedrijfsgegevens</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '.9rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>KvK-nummer</span><strong style={{ color: 'var(--navy)' }}>71298592</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '.9rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>BTW-nummer</span><strong style={{ color: 'var(--navy)' }}>NL002334370B70</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '.9rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Opgericht</span><strong style={{ color: 'var(--navy)' }}>{BUSINESS_STATS.foundedYear} · {BUSINESS_STATS.yearsActive}+ jaar ervaring</strong>
                </div>
              </div>
              <div style={{ marginTop: 28, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
                <h3 style={{ fontSize: '.9rem', color: 'var(--navy)', fontWeight: 700, margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '.06em' }}>Openingstijden</h3>
                {[["Maandag t/m Vrijdag", "07:00, 22:00"], ["Zaterdag & Zondag", "09:00, 20:00"], ["Spoedlijn", "24/7 bereikbaar"]].map(([k, v]) =>
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '.92rem' }}>
                    <span style={{ color: 'var(--text-muted)' }}>{k}</span><strong style={{ color: 'var(--navy)' }}>{v}</strong>
                  </div>
                )}
              </div>
            </div>
            <div className="card" style={{ padding: 32 }}>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--navy)', margin: '0 0 16px' }}>Liever offerte aanvragen?</h2>
              <p style={{ color: 'var(--text-muted)', margin: '0 0 24px' }}>
                Vul in 60 seconden ons formulier in. Wij bellen u binnen 24 uur op werkdagen terug met een vrijblijvende prijs.
              </p>
              <a href={url.offerte()} className="btn-meer" style={{ width: '100%' }}>Naar offerte-formulier →</a>
              <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
                <h3 style={{ fontSize: '.9rem', color: 'var(--navy)', fontWeight: 700, margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '.06em' }}>Certificeringen</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span className="badge badge-trust" style={{ background: 'var(--bg)', color: 'var(--navy)' }}><IconShield width="12" height="12" /> Gecertificeerde partners</span>
                  <span className="badge badge-trust" style={{ background: 'var(--bg)', color: 'var(--navy)' }}><IconShield width="12" height="12" /> IPM gecertificeerd</span>
                </div>
              </div>
            </div>
          </div>
          <style>{`@media (max-width:880px){ .ct-info{ grid-template-columns: 1fr !important; } }`}</style>
        </div>
      </section>

      {/* ─── Wat te verwachten + 4-stappen proces ────────────────────── */}
      {/* Container breder (1200) zodat alle 4 stappen op desktop naast elkaar
          passen — voorheen 920px wat ze tot 3+1 wrap dwong. Grid forceert 4
          kolommen, klapt op mobiel terug naar 2 (≤720) / 1 (≤460) via CSS-
          breakpoints in styles.css op de .contact-stappen class. */}
      <section className="section section-bg">
        <div className="container">
          <div className="sh">
            <div className="section-tag">Wat gebeurt er na contact</div>
            <h2>In 4 stappen van <em>aanvraag tot resultaat</em></h2>
          </div>
          <div className="contact-stappen" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18, marginTop: 16 }}>
            {[
              { n: 1, t: "Aanvraag binnen", d: "Je belt, mailt of vult het formulier in. Wij hebben binnen 30 seconden contact met je (bij bellen) of binnen 24 uur op werkdagen (bij mail/formulier)." },
              { n: 2, t: "Telefonische intake", d: "Korte intake: wat speelt er, waar, hoe urgent, type woning of pand. Wij geven direct een prijsindicatie of vaste prijs aan de telefoon." },
              { n: 3, t: "Afspraak inplannen", d: "Bij spoed: rijden wij vrijwel altijd diezelfde dag uit. Bij planning: 1-3 werkdagen, op een tijd die jou uitkomt. Wij bevestigen telefonisch of via SMS." },
              { n: 4, t: "Behandeling + nazorg", d: "Onze bestrijder arriveert volgens afspraak, voert de behandeling uit en geeft nazorginstructies. Bij sommige plagen volgt een nacontrole na 2-4 weken." },
            ].map(s => (
              <div key={s.n} style={{ background: 'white', borderRadius: 14, padding: 22, border: '1px solid var(--border)', position: 'relative' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--orange)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '1rem', marginBottom: 12 }}>{s.n}</div>
                <h3 style={{ color: 'var(--navy)', fontSize: '1.02rem', margin: '0 0 8px' }}>{s.t}</h3>
                <p style={{ color: 'var(--text)', fontSize: '.92rem', lineHeight: 1.6, margin: 0 }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Werkgebied + reactietijd transparantie ─ premium redesign ─ */}
      <section className="section">
        <div className="container">
          <div className="sh">
            <div className="section-tag">Werkgebied &amp; reactietijd</div>
            <h2>Waar wij <em>actief zijn</em></h2>
            <p style={{ color: 'var(--text-muted)', maxWidth: '62ch', margin: '8px auto 0' }}>
              Dagelijks actief in <strong>117 gemeenten</strong> in Zuid-Holland, Noord-Holland en Utrecht.
              Hoofdvestiging in Den Haag, bestrijders verspreid door het hele werkgebied.
            </p>
          </div>
          {/* Editorial card-grid — zelfde info-card stijl als tarieven-blok
              voor visuele consistentie. Spoed-tijden per regio met icon. */}
          <div className="info-card-grid" style={{ marginTop: 32 }}>
            {[
              { icon: '⚡', regio: 'Den Haag + Westland', tijd: '≤ 4 uur', sub: 'Hoofdvestiging-regio, snelste respons' },
              { icon: '⚡', regio: 'Rotterdam + Schiedam', tijd: '≤ 6 uur', sub: 'Eigen team in Rotterdam-stad' },
              { icon: '⚡', regio: 'Leiden + Delft + Zoetermeer', tijd: '≤ 8 uur', sub: 'Vaste partners in regio' },
              { icon: '📍', regio: 'Amsterdam + Haarlem', tijd: '≤ 12 uur', sub: 'Noord-Holland coverage' },
              { icon: '📍', regio: 'Utrecht + Amersfoort', tijd: '≤ 12 uur', sub: 'Utrecht-stad + omgeving' },
              { icon: '🗓', regio: 'Reguliere planning', tijd: '1-3 werkdagen', sub: 'Heel werkgebied, niet-urgent' },
            ].map(r => (
              <div key={r.regio} className="info-card info-card--time">
                <div className="info-card-icon" aria-hidden="true">{r.icon}</div>
                <div className="info-card-body">
                  <div className="info-card-label">{r.regio}</div>
                  <div className="info-card-value">{r.tijd}</div>
                  <div className="info-card-sub">{r.sub}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '.92rem', lineHeight: 1.7, textAlign: 'center', maxWidth: '64ch', margin: '28px auto 0' }}>
            Niet in deze lijst? Wij komen overal in ZH, NH en Utrecht. Buiten dit werkgebied (Brabant,
            Gelderland, Zeeland, Friesland) bedienen wij niet, bel gerust voor een doorverwijzing naar
            een collega-bedrijf dat wel in jouw regio actief is.
          </p>
        </div>
      </section>

      {/* ─── Contact-FAQ ─────────────────────────────────────────────── */}
      <section className="section faq-section section-bg">
        <div className="container" style={{ maxWidth: 860 }}>
          <div className="sh">
            <div className="section-tag">Veelgestelde vragen</div>
            <h2>FAQ over <em>contact</em></h2>
          </div>
          <div className="faq-list">
            {CONTACT_FAQS.map((f, i) => (
              <div key={i} className={"faq-item" + (openFaq === i ? " open" : "")}>
                <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? -1 : i)} aria-expanded={openFaq === i}>
                  <span>{f.q}</span>
                  <span className="faq-icon" aria-hidden="true">+</span>
                </button>
                <div className="faq-answer"><p>{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2026-05-31 — Partner-werving sectie verplaatst van /partners naar
          /contact (pagina opgeheven om navigatie te vereenvoudigen). */}
      <section id="partner-worden" className="section section-bg">
        <div className="container" style={{ maxWidth: 880 }}>
          <div className="sh">
            <div className="section-tag">Voor bestrijders</div>
            <h2 style={{ fontSize: 'clamp(1.7rem,3vw,2.4rem)', color: 'var(--navy)', margin: '10px 0 18px', lineHeight: 1.2 }}>
              Word <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>ABC-partner</em>
            </h2>
            <p style={{ color: 'var(--text)', lineHeight: 1.75, marginBottom: 14, fontSize: '1.02rem' }}>
              Ben je een KPMB-gecertificeerde ongediertebestrijder of ZZP'er en wil je extra leads ontvangen? ABC werkt met een netwerk van regionale partners. Wij doen marketing en eerste klantcontact, jij voert de behandeling uit. Geen contractbinding langer dan 3 maanden, transparante prijs per geaccepteerde lead.
            </p>
            <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 }}>
              <li style={{ display: 'flex', gap: 10 }}><span style={{ color: 'var(--orange)', fontWeight: 800 }}>✓</span><span style={{ color: 'var(--text)' }}>5-80 leads per maand, afhankelijk van regio en capaciteit</span></li>
              <li style={{ display: 'flex', gap: 10 }}><span style={{ color: 'var(--orange)', fontWeight: 800 }}>✓</span><span style={{ color: 'var(--text)' }}>€25 per geaccepteerde lead, geen voorinvestering</span></li>
              <li style={{ display: 'flex', gap: 10 }}><span style={{ color: 'var(--orange)', fontWeight: 800 }}>✓</span><span style={{ color: 'var(--text)' }}>90 dagen proefperiode met max 3 leads/dag</span></li>
              <li style={{ display: 'flex', gap: 10 }}><span style={{ color: 'var(--orange)', fontWeight: 800 }}>✓</span><span style={{ color: 'var(--text)' }}>Eigen tarieven, jij bepaalt prijs richting eindklant</span></li>
            </ul>
            <p style={{ color: 'var(--text)', lineHeight: 1.7, marginBottom: 18, fontSize: '0.96rem' }}>
              Interesse? Stuur een mail naar <a href="mailto:partners@abcongedierte.nl" style={{ color: 'var(--orange)', textDecoration: 'underline' }}>partners@abcongedierte.nl</a> met je KvK-nummer, regio en certificeringen. Wij nemen binnen 5 werkdagen contact op voor een intake-gesprek.
            </p>
            <a href="mailto:partners@abcongedierte.nl?subject=Aanmelding%20als%20partner" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', background: 'var(--orange)', color: 'white', borderRadius: 10, textDecoration: 'none', fontWeight: 800, fontSize: '0.95rem' }}>
              ✉ Mail partners@abcongedierte.nl
            </a>
          </div>
        </div>
      </section>
    </div>);

}

// ──────────────────────────────────────────────────────────────────────

export function OffertePage() {
  useSEO({
    title: "Gratis offerte aanvragen",
    description: "Gratis en vrijblijvende offerte voor ongediertebestrijding. Wij bellen binnen 24u op werkdagen terug. Vaste prijs vooraf, gecertificeerde partners.",
    path: "/offerte",
    jsonLd: buildBreadcrumb([{ name: "Home", url: "/" }, { name: "Offerte", url: "/offerte" }])
  });

  const [form, setForm] = useState({ naam: "", telefoon: "", email: "", postcode: "", ongedierte: "", omschrijving: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitError('');
    const formNaam = `Contactformulier ${form.ongedierte || 'Algemeen'}`;
    const { ok, error } = await submitForm({
      subject: formNaam,
      formName: formNaam,
      fields: {
        Naam: form.naam,
        Telefoon: form.telefoon,
        Email: form.email,
        Postcode: form.postcode,
        Ongedierte: form.ongedierte,
        Omschrijving: form.omschrijving,
      },
    });
    setSubmitting(false);
    if (ok) setSent(true);
    else {
      setSubmitError('Versturen mislukt. Bel ons direct op 085 500 50 50.');
      console.error('[OffertePage]', error);
    }
  };

  return (
    <div className="page-anim">
      <section className="page-hero">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="breadcrumb">
            <a href={url.home()}>Home</a><span className="sep">/</span>
            <span style={{ color: 'rgba(255,255,255,.9)' }}>Offerte</span>
          </div>
          <h1 style={{ fontSize: 'clamp(2rem,4vw,3.2rem)', fontWeight: 900, color: 'white', lineHeight: 1.1, margin: '14px 0 14px' }}>
            Vraag gratis <span style={{ color: 'var(--orange)' }}>offerte aan</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,.78)', fontSize: '1.05rem', maxWidth: '54ch' }}>
            Vul in 60 seconden in. Wij bellen u binnen 24 uur terug met een vrijblijvende prijs.
          </p>
          <div style={{ marginTop: 18 }}>
            <RatingBar />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container" style={{ maxWidth: 720 }}>
          <div className="form-box">
            {sent ?
            <div style={{ textAlign: 'center', padding: 24 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderRadius: 999, background: '#E8F8EE', color: 'var(--green)', fontWeight: 700, fontSize: 13 }}>
                  <IconCheck /> AANVRAAG BINNEN
                </div>
                <h3 style={{ margin: '20px 0 10px', color: 'var(--navy)' }}>Bedankt! Wij bellen u terug.</h3>
                <p style={{ color: 'var(--text-muted)' }}>Binnen 24 uur op werkdagen. Spoed? Bel <strong style={{ color: 'var(--orange)' }}>085 500 50 50</strong>.</p>
              </div> :

            <form onSubmit={submit}>
                <h3>Offerte aanvragen</h3>
                <p>Geen verplichtingen, reactie binnen 24 uur op werkdagen.</p>
                <div className="form-row">
                  <div className="fg"><label>Voornaam *</label><input type="text" value={form.naam} onChange={upd('naam')} placeholder="Jan" required /></div>
                  <div className="fg"><label>Telefoon *</label><input type="tel" value={form.telefoon} onChange={upd('telefoon')} placeholder="06 12 34 56 78" required /></div>
                </div>
                <div className="fg"><label>E-mailadres *</label><input type="email" value={form.email} onChange={upd('email')} placeholder="jouw@email.nl" required /></div>
                <div className="form-row">
                  <div className="fg"><label>Postcode</label><input type="text" value={form.postcode} onChange={upd('postcode')} placeholder="1234 AB" /></div>
                  <div className="fg"><label>Ongedierte *</label>
                    <select aria-label="Type ongedierte" value={form.ongedierte} onChange={upd('ongedierte')} required>
                      <option value="">Selecteer...</option>
                      {PESTS.map((p) => <option key={p.slug}>{p.nl}</option>)}
                      <option>Anders</option>
                    </select>
                  </div>
                </div>
                <div className="fg"><label>Omschrijving</label><textarea value={form.omschrijving} onChange={upd('omschrijving')} placeholder="Kort omschrijven van het probleem..." /></div>
                <button type="submit" className="btn-verstuur" disabled={submitting}>
                  {submitting ? 'Versturen...' : '✉ Verstuur aanvraag, gratis & vrijblijvend'}
                </button>
                {submitError && (
                  <div role="alert" style={{ marginTop: 10, padding: '10px 14px', background: 'rgba(220,38,38,.08)', border: '1px solid rgba(220,38,38,.3)', borderRadius: 8, color: '#B91C1C', fontSize: '.86rem' }}>
                    {submitError}
                  </div>
                )}
                <p className="form-note">✓ 100% gratis en vrijblijvend, reactie binnen 24 uur</p>
              </form>
            }
          </div>
        </div>
      </section>

      {/* 2026-05-29 — offerte-pagina van ~400 naar ~1100 woorden uitgebreid
          voor betere ranking op "offerte ongediertebestrijding" en variants. */}
      <section className="section section-bg">
        <div className="container" style={{ maxWidth: 1080 }}>
          <div className="sh">
            <div className="section-tag">Wat gebeurt er nu?</div>
            <h2 style={{ fontSize: 'clamp(1.7rem,3vw,2.4rem)', color: 'var(--navy)', margin: '10px 0 22px', lineHeight: 1.2 }}>
              Na uw <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>aanvraag</em> in 4 stappen
            </h2>
          </div>
          <div className="offerte-steps">
            <div className="offerte-step">
              <div className="offerte-step-num">1</div>
              <h3 style={{ fontSize: '1.05rem', color: 'var(--navy)', margin: '0 0 8px' }}>Wij bellen u terug</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem', margin: 0 }}>
                Binnen 24 uur op werkdagen nemen wij contact op om uw situatie kort door te spreken. Geen verkooppraatjes, alleen de feiten die we nodig hebben.
              </p>
            </div>
            <div className="offerte-step">
              <div className="offerte-step-num">2</div>
              <h3 style={{ fontSize: '1.05rem', color: 'var(--navy)', margin: '0 0 8px' }}>Prijs telefonisch</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem', margin: 0 }}>
                Wij geven een eerlijke prijsindicatie aan de telefoon, vóór de afspraak. Zo weet u precies waar u aan toe bent. Geen verrassingen achteraf.
              </p>
            </div>
            <div className="offerte-step">
              <div className="offerte-step-num">3</div>
              <h3 style={{ fontSize: '1.05rem', color: 'var(--navy)', margin: '0 0 8px' }}>Afspraak inplannen</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem', margin: 0 }}>
                Wij plannen een tijdslot dat u uitkomt: ochtend, middag of avond. Bij spoed (allergie, kleine kinderen, NVWA) komen wij vrijwel altijd diezelfde dag.
              </p>
            </div>
            <div className="offerte-step">
              <div className="offerte-step-num">4</div>
              <h3 style={{ fontSize: '1.05rem', color: 'var(--navy)', margin: '0 0 8px' }}>Behandeling + nazorg</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem', margin: 0 }}>
                Onze bestrijder voert de behandeling uit en legt uit wat u kunt verwachten. Na enkele weken neemt onze administratie contact op voor nacontrole.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container" style={{ maxWidth: 880 }}>
          <div className="sh">
            <div className="section-tag">Wat we van u nodig hebben</div>
            <h2 style={{ fontSize: 'clamp(1.7rem,3vw,2.4rem)', color: 'var(--navy)', margin: '10px 0 20px', lineHeight: 1.2 }}>
              Welke informatie helpt voor een <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>scherpe prijs</em>
            </h2>
          </div>
          <p style={{ color: 'var(--text)', lineHeight: 1.75, marginBottom: 14 }}>
            Om u snel een goede prijsindicatie te geven, helpt het als u de volgende dingen weet voordat u belt of het formulier invult. Niet alles is verplicht, maar hoe meer u kunt aangeven, hoe sneller wij u kunnen helpen.
          </p>
          <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ color: 'var(--orange)', fontWeight: 800, flexShrink: 0 }}>✓</span>
              <span style={{ color: 'var(--text)', lineHeight: 1.65 }}><strong style={{ color: 'var(--navy)' }}>Welke plaag.</strong> Muizen, ratten, wespen, bedwantsen, mieren, kakkerlakken, of iets anders. Bij twijfel kunt u ons foto's appen op 085 500 50 50.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ color: 'var(--orange)', fontWeight: 800, flexShrink: 0 }}>✓</span>
              <span style={{ color: 'var(--text)', lineHeight: 1.65 }}><strong style={{ color: 'var(--navy)' }}>Hoe lang u er al last van heeft.</strong> Twee dagen, twee weken, of twee maanden maakt verschil voor de aanpak.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ color: 'var(--orange)', fontWeight: 800, flexShrink: 0 }}>✓</span>
              <span style={{ color: 'var(--text)', lineHeight: 1.65 }}><strong style={{ color: 'var(--navy)' }}>Waar precies.</strong> Zolder, kruipruimte, keuken, slaapkamer, garage, schuur, of buiten in de tuin? Bij bedrijven: welk ruimtetype (kantoor, magazijn, productie, horeca-keuken).</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ color: 'var(--orange)', fontWeight: 800, flexShrink: 0 }}>✓</span>
              <span style={{ color: 'var(--text)', lineHeight: 1.65 }}><strong style={{ color: 'var(--navy)' }}>Type pand en oppervlakte.</strong> Vrijstaande woning, rijtjeshuis, appartement of bedrijfspand. Bij ratten in de tuin helpt het om de tuingrootte te weten.</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ color: 'var(--orange)', fontWeight: 800, flexShrink: 0 }}>✓</span>
              <span style={{ color: 'var(--text)', lineHeight: 1.65 }}><strong style={{ color: 'var(--navy)' }}>Spoed of niet?</strong> Bij allergie, kleine kinderen, of een NVWA-controle voor de deur kunnen wij vaak diezelfde dag uitkomen. Geef het direct aan.</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="section section-bg">
        <div className="container" style={{ maxWidth: 880 }}>
          <div className="sh">
            <div className="section-tag">Veelgestelde vragen</div>
            <h2 style={{ fontSize: 'clamp(1.7rem,3vw,2.4rem)', color: 'var(--navy)', margin: '10px 0 22px', lineHeight: 1.2 }}>
              Veelgestelde vragen over <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>offerte aanvragen</em>
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { q: "Is een offerte echt gratis en vrijblijvend?", a: "Ja, 100% gratis en vrijblijvend. U bent nergens aan verplicht. Wij geven een eerlijke prijsindicatie aan de telefoon. Pas als u akkoord geeft, plannen wij de behandeling in." },
              { q: "Hoe snel reageren jullie op mijn aanvraag?", a: "Op werkdagen reageren wij meestal binnen enkele uren, uiterlijk binnen 24 uur. Op zaterdag en zondag werken wij ook, maar reactietijd kan iets langer zijn. Voor spoed kunt u altijd direct bellen op 085 500 50 50." },
              { q: "Krijg ik vooraf een vaste prijs of pas achteraf?", a: "Wij werken met een vaste prijsindicatie vooraf, telefonisch besproken op basis van uw situatie. Zo voorkomen wij verrassingen. In zeldzame gevallen kan de prijs aangepast worden als de situatie ter plaatse anders is dan beschreven, maar dat bespreken wij dan altijd eerst met u." },
              { q: "Wat als het probleem terugkomt na de behandeling?", a: "Wij werken met vakkundige nacontrole na de behandeling. Komt het probleem terug, dan bekijken wij samen de oorzaak en bespreken wij een vervolgaanpak. Bij wespen geven wij 100% seizoensgarantie." },
              { q: "Werken jullie ook voor bedrijven en horeca?", a: "Ja, wij hebben veel zakelijke klanten in horeca, kantoren, logistiek, zorg en onderwijs. Voor periodieke contracten hebben wij vaste tarieven en afspraken die rekening houden met uw bedrijfsproces (na sluitingstijd, tijdens vakantie, etc.)." },
              { q: "Kunnen jullie ook met spoed komen?", a: "Ja, bij spoedgevallen zoals allergie, kleine kinderen, of een NVWA-controle die voor de deur staat, komen wij vrijwel altijd diezelfde dag. Bel direct 085 500 50 50, dan kijken wij wat mogelijk is." },
            ].map((faq, i) => (
              <details key={i} style={{ background: 'white', borderRadius: 12, border: '1px solid #E5E7EB', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: '1rem', color: 'var(--navy)', listStyle: 'none' }}>
                  {faq.q}
                </summary>
                <p style={{ marginTop: 12, color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>);

}

// ──────────────────────────────────────────────────────────────────────

export function NotFoundPage({ path }) {
  useSEO({
    title: "Pagina niet gevonden",
    description: "Deze pagina kunnen wij niet vinden. Bekijk hieronder onze populairste diensten en steden of bel direct 085 500 50 50.",
    path: "/404"
  });
  const topPlagen = PESTS.slice(0, 6);
  const topSteden = STEDEN.filter(s => s.primary).slice(0, 8);
  return (
    <div className="page-anim">
      <section className="section">
        <div className="container" style={{ maxWidth: 880, padding: '64px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ fontSize: 'clamp(4rem, 12vw, 7rem)', fontWeight: 900, color: 'var(--orange)', lineHeight: 1, marginBottom: 12 }}>404</div>
            <h1 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', color: 'var(--navy)', marginBottom: 12 }}>
              Pagina niet gevonden
            </h1>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: '1.02rem' }}>
              {path
                ? <>We kunnen <code style={{ background: 'var(--bg)', padding: '3px 10px', borderRadius: 6, fontSize: '.92em' }}>{path}</code> niet vinden. Misschien staat er een typo in de URL, of is de pagina verplaatst.</>
                : <>Deze pagina bestaat niet (meer). Misschien hieronder iets nuttigs voor u.</>}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="tel:0855005050" className="btn-bel-lg"><IconPhone width="16" height="16" /> Bel direct 085 500 50 50</a>
              <a href={url.home()} className="btn-form-lg">← Terug naar home</a>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 28 }}>
            <div>
              <h3 style={{ color: 'var(--navy)', fontSize: '1rem', fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                Populaire diensten
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {topPlagen.map(p => (
                  <li key={p.slug}>
                    <a href={url.plaag(p.slug)} style={{ color: 'var(--orange)', textDecoration: 'none', fontWeight: 600 }}>
                      → {p.nl}bestrijding <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '.88rem' }}>vanaf {p.from}</span>
                    </a>
                  </li>
                ))}
                <li>
                  <a href={url.spoed()} style={{ color: 'var(--red, #DC2626)', textDecoration: 'none', fontWeight: 700 }}>
                    🚨 Spoeddienst 24/7
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 style={{ color: 'var(--navy)', fontSize: '1rem', fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                Populaire steden
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {topSteden.map(s => (
                  <li key={s.slug}>
                    <a href={url.stad(s.slug)} style={{ color: 'var(--orange)', textDecoration: 'none', fontWeight: 600 }}>
                      → Ongediertebestrijding {s.naam}
                    </a>
                  </li>
                ))}
                <li>
                  <a href={url.werkgebied()} style={{ color: 'var(--navy)', textDecoration: 'none', fontWeight: 700 }}>
                    Alle 117 gemeenten bekijken →
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>);

}


// ──────────────────────────────────────────────────────────────────────
// DienstenPage — overview of all 6 plagen
// ──────────────────────────────────────────────────────────────────────
export function DienstenPage() {
  useSEO({
    title: "Onze diensten, 15 ongediertesoorten",
    description: "Alle 15 ongediertebestrijding-diensten van ABC: muizen, ratten, wespen, bedwantsen, mieren, kakkerlakken en meer. Gecertificeerde partners, 24/7 spoed, eerlijk tarief vooraf.",
    path: "/diensten",
    jsonLd: buildBreadcrumb([{ name: "Home", url: "/" }, { name: "Diensten", url: "/diensten" }])
  });

  const [pc, setPc] = useState("");
  const formatPc = (raw) => {
    let v = raw.replace(/\s/g, '').toUpperCase();
    if (v.length > 4) v = v.slice(0, 4) + ' ' + v.slice(4, 6);
    return v;
  };
  const searchPostcode = () => {
    const r = pc.trim().replace(/\s/g, '');
    if (r.length < 4) { alert('Vul een geldige postcode in, bijv. 2512 AB'); return; }
    const city = cityForPostcode(r);
    if (city) navigate(`/werkgebied/${city}?pc=${encodeURIComponent(r)}`);
    else navigate(`/offerte?pc=${encodeURIComponent(r)}`);
  };
  const [hf, setHf] = useState({ naam: "", telefoon: "", postcode: "", ongedierte: "" });
  const [hfSent, setHfSent] = useState(false);
  const updHf = k => e => setHf(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="page-anim">
      <section className="hero">
        <div className="container">
          <div className="hero-inner">
            <div>
              <div className="breadcrumb" style={{ marginBottom: 18 }}>
                <a href={url.home()}>Home</a><span className="sep">/</span>
                <span style={{ color: 'rgba(255,255,255,.9)' }}>Diensten</span>
              </div>
              <div className="hero-badge">⚡ Erkend &amp; gecertificeerd</div>
              <h1>
                Onze diensten voor <br/>
                <em>ongediertebestrijding</em>
              </h1>
              <p className="hero-sub">
                Wij bestrijden muizen, ratten, wespen, kakkerlakken, bedwantsen en andere veelvoorkomende plagen. Klik door voor aanpak, prijs en beschikbaarheid.
              </p>
              <HeroPostcodeBar pc={pc} setPc={setPc} formatPc={formatPc} searchPostcode={searchPostcode} />
              <div className="hero-rating-mobile"><RatingBar /></div>
              <div className="hero-trust">
                <div className="tit"><span className="tit-mark">✓</span> Spoeddienst 24/7</div>
                <div className="tit"><span className="tit-mark">✓</span> Snel ter plaatse</div>
                <div className="tit"><span className="tit-mark">✓</span> Vaste prijs vooraf</div>
                <div className="tit"><span className="tit-mark">✓</span> Particulier &amp; zakelijk</div>
              </div>
              <div className="hero-rating-desktop"><RatingBar /></div>
              </div>
            <RotterdamForm variant="hero" />
          </div>
        </div>
      </section>
      {/* ─── Intro-tekst boven plagen-grid voor SEO context ──────────── */}
      <section className="section" style={{ paddingBottom: 0 }}>
        <div className="container" style={{ maxWidth: 860 }}>
          <div className="sh">
            <div className="section-tag">Compleet aanbod</div>
            <h2>Bestrijding van <em>15 ongediertesoorten</em> in Zuid-Holland, Noord-Holland en Utrecht</h2>
          </div>
          <p style={{ color: 'var(--text)', lineHeight: 1.8, marginBottom: 16, fontSize: '1.02rem' }}>
            Of het nu om <strong>knaagdieren</strong> gaat (muizen, ratten), <strong>vliegende insecten</strong> (wespen, vliegen, motten), <strong>kruipende insecten</strong> (kakkerlakken, mieren, spinnen, zilvervisjes), <strong>bloedzuigers</strong> (bedwantsen, vlooien), <strong>tuin-overlast</strong> (mollen) of <strong>hout-aantasters</strong> (houtworm), wij hebben de specialisatie, certificering en ervaring om het probleem definitief op te lossen. Hieronder zie je onze 15 hoofd-diensten, elk met eigen aanpak en vaste prijs vooraf.
          </p>
          <p style={{ color: 'var(--text)', lineHeight: 1.8, marginBottom: 0, fontSize: '1.02rem' }}>
            <strong>Hoe kies je de juiste dienst?</strong> Heb je twijfel over welk ongedierte je hebt? Bel 085 500 50 50 voor gratis telefonisch advies, of mail een foto naar info@abcongedierte.nl, en wij vertellen direct welk type plaag het is én welke aanpak nodig is. Alle 15 diensten zijn beschikbaar voor zowel <strong>particulier als zakelijk</strong> (woningen, horeca, kantoren, scholen, VvE's, distributiecentra).
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          {/* Editorial card-grid — herbruikt PlaagCard.
              Alfabetisch (nl-locale) zodat bezoekers makkelijk vinden
              wat ze zoeken; rij begint met Bedwantsen. */}
          <div className="adc-grid">
            {[...PESTS].sort((a, b) => a.nl.localeCompare(b.nl, 'nl')).map(p => (
              <PlaagCard key={p.slug} plaag={p} href={url.plaag(p.slug)} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Categorieën-uitleg voor SEO + scannability ──────────────── */}
      <section className="section section-bg">
        <div className="container" style={{ maxWidth: 920 }}>
          <div className="sh">
            <div className="section-tag">Diensten per categorie</div>
            <h2>Welke <em>categorie</em> past bij jouw probleem?</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18, marginTop: 20 }}>
            {[
              { cat: "Knaagdieren", icon: "🐭", services: "Muizen · Ratten", desc: "Vermenigvuldigen razendsnel. Knagen door bedrading (brandgevaar), besmetten voedsel. Onze aanpak: bron-opsporing + proofing + mechanische vangkasten." },
              { cat: "Vliegende insecten", icon: "🐝", services: "Wespen · Vliegen · Motten · Rioolvliegjes", desc: "Allergie-risico, voedsel-besmetting, voorraad-schade. Snelle bron-aanpak + soort-specifieke middelen." },
              { cat: "Kruipende insecten", icon: "🪳", services: "Kakkerlakken · Mieren · Spinnen · Zilvervisjes · Papiervisjes", desc: "Vaak verborgen in spleten en achter plinten. Wij sporen schuilplekken op + behandelen met gerichte gel-aas of residuele middelen." },
              { cat: "Bloedzuigers", icon: "🛏️", services: "Bedwantsen · Vlooien", desc: "Steken, jeuk, slaapproblemen. Wij behandelen het hele oppervlak, eieren en larven inbegrepen. Bij bedwantsen ook hittebehandeling." },
              { cat: "Tuin & buiten", icon: "🌱", services: "Mollen", desc: "Bulthopen verwoesten gazon. Wij vangen met mechanische klemtypevangers, voldoet aan wettelijke vangstcriteria." },
              { cat: "Hout-aantasters", icon: "🪵", services: "Houtworm (Anobium + Huisboktor)", desc: "Vreten jarenlang ongezien. Wij injecteren professioneel + behandelen aanliggend hout. Bij huisboktor ook thermische behandeling." },
            ].map(c => (
              <div key={c.cat} style={{ background: 'white', borderRadius: 14, padding: 22, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '1.8rem', marginBottom: 4 }}>{c.icon}</div>
                <h3 style={{ color: 'var(--navy)', fontSize: '1.08rem', margin: '0 0 4px' }}>{c.cat}</h3>
                <div style={{ fontSize: '.82rem', color: 'var(--orange)', fontWeight: 700, marginBottom: 8 }}>{c.services}</div>
                <p style={{ color: 'var(--text)', fontSize: '.92rem', lineHeight: 1.6, margin: 0 }}>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>);
}

// ──────────────────────────────────────────────────────────────────────
// WerkgebiedPage — overview of all cities with hero offerte-form + postcode
// ──────────────────────────────────────────────────────────────────────
export function WerkgebiedPage() {
  useSEO({
    title: `Ongediertebestrijding in ${BUSINESS_STATS.municipalitiesServed} gemeenten in NH, ZH & Utrecht`,
    description: `ABC is dagelijks actief in ${BUSINESS_STATS.municipalitiesServed} gemeenten in Zuid-Holland, Noord-Holland en Utrecht. Gemiddeld binnen 24 uur op locatie.`,
    path: "/werkgebied",
    jsonLd: buildBreadcrumb([{ name: "Home", url: "/" }, { name: "Werkgebied", url: "/werkgebied" }])
  });

  // Postcode-zoeker zelfde gedrag als op de homepage
  const [pc, setPc] = useState("");
  const formatPc = (raw) => {
    let v = raw.replace(/\s/g, '').toUpperCase();
    if (v.length > 4) v = v.slice(0, 4) + ' ' + v.slice(4, 6);
    return v;
  };
  const searchPostcode = () => {
    const r = pc.trim().replace(/\s/g, '');
    if (r.length < 4) { alert('Vul een geldige postcode in, bijv. 2512 AB'); return; }
    const city = cityForPostcode(r);
    if (city) {
      navigate(`/werkgebied/${city}?pc=${encodeURIComponent(r)}`);
    } else {
      navigate(`/offerte?pc=${encodeURIComponent(r)}`);
    }
  };

  // Offerte-form state — identiek aan home
  const [hf, setHf] = useState({ naam: "", telefoon: "", postcode: "", ongedierte: "" });
  const [hfSent, setHfSent] = useState(false);
  const updHf = k => e => setHf(f => ({ ...f, [k]: e.target.value }));

  const provinces = [
    { naam: "Zuid-Holland", filter: (s) => s.provincie === "Zuid-Holland" },
    { naam: "Noord-Holland", filter: (s) => s.provincie === "Noord-Holland" },
    { naam: "Utrecht", filter: (s) => s.provincie === "Utrecht" }
  ];

  return (
    <div className="page-anim">
      <section className="hero">
        <div className="container">
          <div className="hero-inner">
            <div>
              <div className="breadcrumb" style={{ marginBottom: 18 }}>
                <a href={url.home()}>Home</a><span className="sep">/</span>
                <span style={{ color: 'rgba(255,255,255,.9)' }}>Werkgebied</span>
              </div>
              <div className="hero-badge">⚡ Erkend &amp; gecertificeerd</div>
              <h1>
                Ons werkgebied in <br/>
                <em>de Randstad</em>
              </h1>
              <p className="hero-sub">
                Wij zijn dagelijks actief in Zuid-Holland, Noord-Holland en Utrecht, gemiddeld binnen 24 uur op locatie bij u thuis of zakelijk pand.
              </p>
              <HeroPostcodeBar pc={pc} setPc={setPc} formatPc={formatPc} searchPostcode={searchPostcode} />
              <div className="hero-rating-mobile"><RatingBar /></div>
              <div className="hero-trust">
                <div className="tit"><span className="tit-mark">✓</span> Spoeddienst 24/7</div>
                <div className="tit"><span className="tit-mark">✓</span> Snel ter plaatse</div>
                <div className="tit"><span className="tit-mark">✓</span> Vaste prijs vooraf</div>
                <div className="tit"><span className="tit-mark">✓</span> Particulier &amp; zakelijk</div>
              </div>
              <div className="hero-rating-desktop"><RatingBar /></div>
              </div>

            <RotterdamForm variant="hero" />
          </div>
        </div>
      </section>

      <section className="section werkgebied-prov">
        <div className="container">
          {provinces.map((p, i) => {
            const cities = STEDEN.filter(p.filter);
            if (!cities.length) return null;
            return (
              <div key={p.naam} className="wgp-prov" data-prov={i}>
                <div className="wgp-prov-head">
                  <div className="wgp-prov-titlewrap">
                    <h2 className="wgp-prov-name">{p.naam}</h2>
                    <span className="wgp-prov-meta">{cities.length} steden &amp; gemeenten</span>
                  </div>
                  <span className="wgp-prov-bar" aria-hidden="true"></span>
                </div>

                <ul className="wgp-city-list">
                  {cities.map((s) => (
                    <li key={s.slug}>
                      <a href={url.stad(s.slug)} className="wgp-city">
                        <span className="wgp-city-name">{s.naam}</span>
                        <span className="wgp-city-arrow" aria-hidden="true">→</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* 2026-05-29 — werkgebied-pagina van ~580 naar ~1500 woorden uitgebreid
          voor betere ranking op "ongediertebestrijder zuid-holland" en
          variants. Sectie per provincie met regio-specifieke aanpak,
          reactietijden, en doelgroepen (particulier/zakelijk/horeca/VvE). */}
      <section className="section section-bg">
        <div className="container" style={{ maxWidth: 960 }}>
          <div className="sh">
            <div className="section-tag">Onze regio's</div>
            <h2 style={{ fontSize: 'clamp(1.7rem,3vw,2.4rem)', color: 'var(--navy)', margin: '10px 0 22px', lineHeight: 1.2 }}>
              Hoe wij <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>per provincie</em> werken
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            <div>
              <h3 style={{ fontSize: '1.3rem', color: 'var(--navy)', margin: '0 0 10px' }}>Zuid-Holland: ons grootste werkgebied</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.75, marginBottom: 12 }}>
                Zuid-Holland is de meest bevolkte provincie van Nederland en daarmee onze drukste regio. Wij bedienen alle grote steden (Rotterdam, Den Haag, Leiden, Dordrecht, Delft) plus alle omliggende gemeenten. Reactietijd in dit gebied is gemiddeld 12 tot 24 uur, in de Drechtsteden en Rijnmond vaak nog sneller. Dagelijks rijdt onze bestrijders vanaf Den Haag, Rotterdam en Leiden uit naar oproepen.
              </p>
              <p style={{ color: 'var(--text)', lineHeight: 1.75 }}>
                Specifieke aandacht in Zuid-Holland: rattenoverlast langs de Nieuwe Maas, IJssel en Rijn (havengebieden), bedwantsen in Rotterdamse hotels en Haagse ambassadepanden, en muizen in studentenpanden van Leiden en Delft. Voor horeca en VvE's bieden wij periodieke contracten.
              </p>
            </div>

            <div>
              <h3 style={{ fontSize: '1.3rem', color: 'var(--navy)', margin: '0 0 10px' }}>Noord-Holland: van Amsterdam tot kop van Noord-Holland</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.75, marginBottom: 12 }}>
                In Noord-Holland zijn wij actief in Amsterdam (alle stadsdelen), Haarlem, Zaanstad, Amstelveen, en de gehele kop tot Den Helder. De combinatie van internationale toerisme, eeuwenoude grachtenpanden en moderne nieuwbouw zorgt voor uiteenlopende ongedierterisico's. In Amsterdam zien wij gemiddeld het hoogste aantal bedwantsen-meldingen van Nederland door de hoge toerismedichtheid en Airbnb-verhuur.
              </p>
              <p style={{ color: 'var(--text)', lineHeight: 1.75 }}>
                Voor Amsterdam en omgeving is onze reactietijd gemiddeld 12 tot 24 uur. In de Zaanstreek en kop van Noord-Holland soms 24 tot 48 uur, afhankelijk van afstand. Wij werken intensief met Waternet voor publieke rattenmeldingen en met VvE's voor collectieve aanpak van flatcomplexen.
              </p>
            </div>

            <div>
              <h3 style={{ fontSize: '1.3rem', color: 'var(--navy)', margin: '0 0 10px' }}>Utrecht: studentenstad en Vinex-groei</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.75, marginBottom: 12 }}>
                In Utrecht bedienen wij de stad Utrecht, Amersfoort, Nieuwegein, Zeist, Houten, Veenendaal en alle omliggende gemeenten. De provincie combineert historische binnenstad (werfkelders Oudegracht), grote studentenpopulatie en explosieve Vinex-groei in Leidsche Rijn, Vleuten en Vathorst. Onze bestrijders kennen de unieke problematiek per gebied.
              </p>
              <p style={{ color: 'var(--text)', lineHeight: 1.75 }}>
                Reactietijd in Utrecht-stad gemiddeld 12 tot 24 uur. Voor Amersfoort en omliggende gemeenten 24 uur. Voor Veluwerand-gemeenten (Veenendaal, Renswoude) soms iets langer. In nieuwbouwwijken adviseren wij standaard een muizencontrole binnen 18 maanden na oplevering, omdat kieren rondom leidingdoorvoeren vaak pas dan zichtbaar worden.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container" style={{ maxWidth: 960 }}>
          <div className="sh">
            <div className="section-tag">Onze klanten</div>
            <h2 style={{ fontSize: 'clamp(1.7rem,3vw,2.4rem)', color: 'var(--navy)', margin: '10px 0 18px', lineHeight: 1.2 }}>
              Voor wie wij <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>werken</em>
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, maxWidth: 880 }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--navy)', margin: '0 0 8px' }}>Particulieren</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                Huiseigenaren en huurders met overlast van muizen, ratten, wespen, bedwantsen of andere plagen. Spoeddienst beschikbaar bij allergie, kleine kinderen of acute overlast.
              </p>
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--navy)', margin: '0 0 8px' }}>Bedrijven</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                Logistiek, kantoor, magazijn en industrie. Periodieke contracten voor preventieve bestrijding, audit-ready voor ISO en HACCP-trajecten.
              </p>
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--navy)', margin: '0 0 8px' }}>Horeca</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                Restaurants, café's, hotels, hostels en cateraars. NVWA-conforme aanpak, behandelingen na sluitingstijd, preventiecontract voor periodieke keuringen.
              </p>
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--navy)', margin: '0 0 8px' }}>VvE's en verhuurders</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                Collectieve aanpak voor flatcomplexen, studentenpanden, monumentale panden. Wij coördineren met de VvE en bewoners zodat alle units tegelijk worden behandeld.
              </p>
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--navy)', margin: '0 0 8px' }}>Zorginstellingen</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                Verpleeghuizen, ziekenhuizen, kindercentra. NEN-conforme behandeling, geen chemische resten in bewonersgebieden, planning rondom rooster en bezoekuren.
              </p>
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', color: 'var(--navy)', margin: '0 0 8px' }}>Onderwijs</h3>
              <p style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                Universiteiten, hogescholen, scholen. Discrete service tijdens lesuren, Engelstalige communicatie voor internationaal personeel, preventief voor kantines.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-bg">
        <div className="container" style={{ maxWidth: 880 }}>
          <div className="sh">
            <div className="section-tag">Veelgestelde vragen</div>
            <h2 style={{ fontSize: 'clamp(1.7rem,3vw,2.4rem)', color: 'var(--navy)', margin: '10px 0 22px', lineHeight: 1.2 }}>
              Veelgestelde vragen over ons <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>werkgebied</em>
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { q: "In welke provincies werken jullie?", a: "Wij zijn dagelijks actief in heel Zuid-Holland, Noord-Holland en Utrecht. In totaal bedienen wij meer dan honderd gemeenten in deze drie provincies." },
              { q: "Wat is jullie gemiddelde reactietijd?", a: "Voor de Randstad gemiddeld 12 tot 24 uur. Voor randgemeenten 24 tot 48 uur, afhankelijk van afstand en planning. Bij spoed (allergie, NVWA-controle, kleine kinderen) streven wij naar nog snellere respons." },
              { q: "Komen jullie ook bij mij als ik buiten de Randstad woon?", a: "Bel ons gerust: wij kijken altijd of wij iemand in de buurt hebben. Voor gemeenten op de rand van Noord-Holland (kop van NH) of Utrecht (Veluwerand) hebben wij vaak een vaste partner in de buurt." },
              { q: "Werken jullie in het hele weekend?", a: "Ja, wij zijn 7 dagen per week telefonisch bereikbaar van maandag tot en met zondag van 07:00 tot 22:00. Spoeddiensten zijn 24/7 beschikbaar." },
              { q: "Hoe regelt het werkgebied de aanpak voor verschillende ongediertesoorten?", a: "Onze bestrijders zijn allround opgeleid. Voor specialistische plagen (zoals bedwantsen via hittebehandeling) zetten wij specialisten in. Bij internationale logistiek werken wij met meertalige bestrijders." },
              { q: "Kan ik een vaste bestrijder krijgen?", a: "Voor periodieke contracten (horeca, VvE, bedrijf) werken wij waar mogelijk met vaste bestrijders zodat continuïteit en kennis van uw situatie behouden blijft." },
            ].map((faq, i) => (
              <details key={i} style={{ background: 'white', borderRadius: 12, border: '1px solid #E5E7EB', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: '1rem', color: 'var(--navy)', listStyle: 'none' }}>
                  {faq.q}
                </summary>
                <p style={{ marginTop: 12, color: 'var(--text)', lineHeight: 1.7, fontSize: '0.96rem' }}>
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>);
}

// ──────────────────────────────────────────────────────────────────────
// OverOnsPage — E-E-A-T trust signal (Experience, Expertise, Authoritativeness, Trustworthiness)
// Google waardeert dit zwaar voor lokale-service queries.
// ──────────────────────────────────────────────────────────────────────
export function OverOnsPage() {
  useSEO({
    title: "Over ABC Ongediertebestrijding",
    description: `Sinds 2017 actief in ongediertebestrijding in ZH, NH en Utrecht. Wij werken uitsluitend met gecertificeerde plaagdierspecialisten. ${REVIEWS.total} klanten gaven ${String(REVIEWS.rating).replace('.', ',')}/5.`,
    path: "/over-ons",
    jsonLd: [
      buildBreadcrumb([{ name: "Home", url: "/" }, { name: "Over ons", url: "/over-ons" }]),
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "mainEntity": { "@id": "https://www.abcongedierte.nl/#organization" },
        "datePublished": "2017-01-01",
        "dateModified": "2026-05-19"
      }
    ]
  });

  return (
    <div className="page-anim">
      <section className="section" style={{ paddingTop: 56 }}>
        <div className="container" style={{ maxWidth: 920 }}>
          <div className="breadcrumb" style={{ marginBottom: 18, color: 'var(--text-muted)' }}>
            <a href={url.home()}>Home</a><span className="sep" style={{ margin: '0 8px', opacity: .5 }}>/</span>
            <span style={{ color: 'var(--navy)', fontWeight: 600 }}>Over ons</span>
          </div>
          <h1 style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.4rem)', color: 'var(--navy)', marginBottom: 8 }}>Over ABC Ongediertebestrijding</h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: 36, fontSize: '1.02rem' }}>
            Sinds 2017 dé specialist voor ongediertebestrijding in Zuid-Holland, Noord-Holland en Utrecht, uitsluitend werkend met gecertificeerde plaagdierspecialisten. <strong>{REVIEWS.total} klanten</strong> gaven ons gemiddeld <strong>{String(REVIEWS.rating).replace('.', ',')}/5 sterren</strong> op Google.
          </p>

          {/* Statistieken-strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 18, marginBottom: 48 }}>
            {[
              { n: `${BUSINESS_STATS.yearsActive}+`, l: "Jaar ervaring" },
              { n: `${BUSINESS_STATS.customersPerYear.toLocaleString('nl-NL')}+`, l: "Klanten per jaar" },
              { n: String(BUSINESS_STATS.municipalitiesServed), l: "Gemeenten in werkgebied" },
              { n: "24/7", l: "Spoeddienst beschikbaar" },
            ].map(s => (
              <div key={s.l} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, textAlign: 'center' }}>
                <div style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--orange)', lineHeight: 1 }}>{s.n}</div>
                <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginTop: 6, fontWeight: 600 }}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Verhaal */}
          <h2 style={{ fontSize: '1.3rem', color: 'var(--navy)', marginBottom: 12 }}>Hoe het begon</h2>
          <p style={{ color: 'var(--text)', lineHeight: 1.8, marginBottom: 18 }}>
            ABC Ongediertebestrijding is in 2017 opgericht in Den Haag uit frustratie over de bestaande markt: lange wachttijden, vage prijzen, en bestrijders die alleen sproeien zonder de oorzaak aan te pakken. Wij wilden het anders doen: <strong>snel, transparant, en met bron-aanpak</strong>.
          </p>
          <p style={{ color: 'var(--text)', lineHeight: 1.8, marginBottom: 28 }}>
            Vandaag bedienen onze gecertificeerde bestrijders dagelijks woningen, horeca, kantoren en scholen in 117 gemeenten, van Brielle tot Texel, van Wassenaar tot Eemnes. Wij behandelen jaarlijks meer dan 1.500 klanten, met een gemiddelde reactietijd van minder dan 24 uur.
          </p>

          {/* Onze werkwijze */}
          <h2 style={{ fontSize: '1.3rem', color: 'var(--navy)', marginBottom: 12 }}>Onze werkwijze</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18, marginBottom: 36 }}>
            {[
              { t: "Inspectie", d: "Wij bezoeken uw locatie en vinden de oorzaak, niet alleen de symptomen." },
              { t: "Vaste prijs", d: "Aan de telefoon krijgt u al een prijsindicatie. Geen verrassingen achteraf." },
              { t: "Vakkundige aanpak", d: "CTGB-gecertificeerde middelen, veilig voor mens en huisdier wanneer correct toegepast." },
              { t: "Wespen-seizoensgarantie", d: "Op wespennesten geven wij 100% garantie binnen het seizoen. Bij andere ongediertesoorten werken wij met vakkundige nacontrole." },
            ].map(s => (
              <div key={s.t} style={{ padding: 18, border: '1px solid var(--border)', borderRadius: 12, background: 'white' }}>
                <h3 style={{ color: 'var(--orange)', fontSize: '1rem', marginBottom: 6 }}>{s.t}</h3>
                <p style={{ color: 'var(--text)', fontSize: '.95rem', lineHeight: 1.6, margin: 0 }}>{s.d}</p>
              </div>
            ))}
          </div>

          {/* Wat wij anders doen */}
          <h2 style={{ fontSize: '1.3rem', color: 'var(--navy)', marginBottom: 12 }}>Wat wij anders doen</h2>
          <p style={{ color: 'var(--text)', lineHeight: 1.8, marginBottom: 18 }}>
            De ongediertebranche staat bekend om <strong>vage prijzen, lange wachttijden en bestrijders die alleen sproeien zonder oorzaak op te lossen</strong>. Wij doen het op drie punten anders. Ten eerste werken wij <strong>met vaste prijs vooraf</strong>, al tijdens de eerste telefonische intake geven wij een definitieve prijs-bandbreedte. Geen toeslagen, geen "voorrijkosten", geen verrassingen op de factuur.
          </p>
          <p style={{ color: 'var(--text)', lineHeight: 1.8, marginBottom: 18 }}>
            Ten tweede zoeken wij <strong>altijd naar de bron</strong>, niet alleen de zichtbare plaag. Wij dichten muizen-ingangen af, wij behandelen de broedplaats van vliegjes, wij sporen de looproutes van ratten op. Sproeien zonder bron-aanpak betekent dat de plaag binnen 6 weken terug is. Onze aanpak voorkomt dat door eerst de oorzaak weg te nemen.
          </p>
          <p style={{ color: 'var(--text)', lineHeight: 1.8, marginBottom: 36 }}>
            Ten derde zijn wij <strong>reëel transparant over wat wel en niet werkt</strong>. Veel concurrenten beloven garanties die ze niet kunnen waarmaken, wij geven alleen 100% garantie op wespen (omdat dat seizoensgebonden werkt) en zijn open over de grenzen bij andere plagen. Bij papiervisjes vertellen wij eerlijk dat 2-3 behandelingen nodig zijn. Bij mollen dat herkolonisatie vanuit aangrenzende tuinen niet uit te sluiten valt. Eerlijkheid bouwt vertrouwen op, en dat zien wij terug in onze {REVIEWS.ratingStr}/5 sterren over {REVIEWS.total}+ Google reviews.
          </p>

          {/* Duurzaamheid */}
          <h2 style={{ fontSize: '1.3rem', color: 'var(--navy)', marginBottom: 12 }}>Onze visie op duurzaamheid</h2>
          <p style={{ color: 'var(--text)', lineHeight: 1.8, marginBottom: 36 }}>
            Ongediertebestrijding heeft impact op het ecosysteem. Wij volgen daarom de <strong>IPM-methodiek (Integrated Pest Management)</strong>: eerst preventie en monitoring, dan mechanische methodes (vangkasten, proofing), en pas als laatste chemische middelen. Bij ratten gebruiken wij standaard mechanische vangkasten in plaats van gif, dit voorkomt secundaire vergiftiging van roofvogels en huisdieren. Onze CTGB-gecertificeerde middelen kiezen wij zo gericht mogelijk: liever 5 milligram precies op de bron dan 50 milliliter sproeien. Bij wespennesten doen wij geen "preventieve" behandelingen, alleen waar er een actief nest is.
          </p>

          {/* Certificering */}
          <h2 style={{ fontSize: '1.3rem', color: 'var(--navy)', marginBottom: 12 }}>Certificering &amp; erkenningen</h2>
          <ul style={{ color: 'var(--text)', lineHeight: 1.85, marginBottom: 36, paddingLeft: 22 }}>
            <li><strong>Gecertificeerde partners</strong>, wij werken uitsluitend met IPM-gecertificeerde plaagdierspecialisten en CTGB-gecertificeerde bestrijdingsmiddelen, die voldoen aan de hoogste branchestandaarden</li>
            <li><strong>IPM gecertificeerd</strong>, Integrated Pest Management, gericht op duurzame, milieubewuste bestrijding</li>
            <li><strong>CTGB</strong>, wij werken alleen met door het College voor de Toelating van Gewasbeschermingsmiddelen en Biociden goedgekeurde middelen</li>
            <li><strong>NVWA-compliant</strong>, alle behandelingen worden gelogd voor horeca, voedselindustrie en zorginstellingen</li>
          </ul>

          {/* Klantbeloftes */}
          <h2 style={{ fontSize: '1.3rem', color: 'var(--navy)', marginBottom: 12 }}>Onze beloftes aan jou</h2>
          <p style={{ color: 'var(--text)', lineHeight: 1.8, marginBottom: 12 }}>
            Wij doen <strong>vier concrete beloftes</strong> aan elke klant:
          </p>
          <ul style={{ color: 'var(--text)', lineHeight: 1.85, marginBottom: 36, paddingLeft: 22 }}>
            <li><strong>Reactietijd binnen 24 uur</strong> in heel ons werkgebied. In Den Haag en Rotterdam vaak binnen 4-6 uur bij spoed.</li>
            <li><strong>Vaste prijs vooraf</strong>, geen verborgen kosten op de factuur, geen verrassingen achteraf.</li>
            <li><strong>Eén aanspreekpunt</strong>, dezelfde bestrijder die de inspectie doet, voert ook de behandeling uit en doet de nacontrole. Geen team-wisselingen, geen miscommunicatie.</li>
            <li><strong>Discrete service</strong>, bij horeca, kantoren en VvE's komen wij in neutrale kleding, met onopvallend voertuig waar gewenst. Buren of klanten merken niets.</li>
          </ul>

          {/* E-E-A-T: Author / Bedrijfsgegevens */}
          <h2 style={{ fontSize: '1.3rem', color: 'var(--navy)', marginBottom: 12 }}>Bedrijfsgegevens</h2>
          <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 22, marginBottom: 36 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, fontSize: '.95rem' }}>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: '.82rem', marginBottom: 2 }}>Bedrijfsnaam</div>
                <strong style={{ color: 'var(--navy)' }}>ABC Ongediertebestrijding b.v.</strong>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: '.82rem', marginBottom: 2 }}>KvK-nummer</div>
                <strong style={{ color: 'var(--navy)' }}>71298592</strong>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: '.82rem', marginBottom: 2 }}>BTW-nummer</div>
                <strong style={{ color: 'var(--navy)' }}>NL002334370B70</strong>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: '.82rem', marginBottom: 2 }}>Opgericht</div>
                <strong style={{ color: 'var(--navy)' }}>{BUSINESS_STATS.foundedYear} ({BUSINESS_STATS.yearsActive}+ jaar ervaring)</strong>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: '.82rem', marginBottom: 2 }}>Hoofdvestiging</div>
                <strong style={{ color: 'var(--navy)' }}>Fuchsiastraat 55, 2565 PL Den Haag</strong>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: '.82rem', marginBottom: 2 }}>Bereikbaar</div>
                <strong style={{ color: 'var(--navy)' }}>085 500 50 50 · info@abcongedierte.nl</strong>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div style={{ background: 'linear-gradient(135deg, var(--navy) 0%, var(--navy-deep, #0F1E38) 100%)', color: 'white', borderRadius: 16, padding: 32, textAlign: 'center' }}>
            <h3 style={{ color: 'white', marginBottom: 8 }}>Klaar om kennis te maken?</h3>
            <p style={{ color: 'rgba(255,255,255,.75)', marginBottom: 22 }}>Bel direct of vraag een gratis offerte aan, wij zijn er voor u.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="tel:0855005050" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 24px', background: 'var(--orange)', color: 'white', textDecoration: 'none', borderRadius: 10, fontWeight: 700 }}>📞 085 500 50 50</a>
              <a href={url.offerte()} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 24px', background: 'rgba(255,255,255,.10)', color: 'white', textDecoration: 'none', borderRadius: 10, fontWeight: 700, border: '1px solid rgba(255,255,255,.25)' }}>Gratis offerte →</a>
            </div>
          </div>
        </div>
      </section>
    </div>);
}


// ──────────────────────────────────────────────────────────────────────
// HorecaPage — B2B-landingpage voor horeca/HACCP-pakketten.
// Hoofdcategorieën klanten: restaurants, café's, hotels, bedrijfskantines,
// catering, foodtrucks. Doel: kwartaalcontracten of incidentele HACCP-aanpak.
// ──────────────────────────────────────────────────────────────────────
export function HorecaPage() {
  const HORECA_FAQS = [
    { q: "Wat verlangt NVWA precies van mijn horeca-zaak?",
      a: "NVWA verwacht een schriftelijk ongediertepreventie-plan, een logboek van waarnemingen en behandelingen, een contract met een erkende bestrijder, visuele inspectie minimaal 1× per kwartaal (keuken, opslag, kruipruimte, dakgoten) en lokstations met monitoringkaart op niet-voedselzones. Voor HACCP-gecertificeerde keukens is dit een hard eis, geen optie." },
    { q: "Wat doet ABC voor mijn HACCP-administratie?",
      a: "Wij voeren de visuele inspectie en behandeling vakkundig uit en bespreken na elk bezoek de bevindingen met u. Het bijhouden van uw HACCP-logboek doet u zelf (of uw HACCP-adviseur). Wij geven u alle informatie die u daarvoor nodig heeft: wat we hebben aangetroffen, welke maatregelen we hebben uitgevoerd, en eventuele aandachtspunten voor uw preventieplan." },
    { q: "Hoe vaak komt er een controle langs?",
      a: "Standaard: 4× per jaar (kwartaalcontrole). Voor risicovollere bedrijven (foodtrucks, openbare keukens, oudere panden) bieden wij maandelijkse rondes. Tussen geplande bezoeken altijd 24/7 spoedlijn bij meldingen, geen extra factuur voor uitruk-bezoek bij contract-klanten." },
    { q: "Wat kost een HACCP-preventiecontract?",
      a: "Afhankelijk van pand-grootte, type horeca en aantal locaties. Een kleine zaak met één keuken kost beduidend minder dan een keten of een grote keuken met meerdere risicovolle ruimtes. Definitieve prijs altijd vooraf vastgelegd in contract, geen verrassingen achteraf. Bel ons voor een gratis intake en offerte." },
    { q: "Worden alle plagen gedekt in een contract?",
      a: "Het standaardcontract dekt de typische horeca-plagen: ratten, muizen, kakkerlakken, vliegen, rioolvliegjes, mieren. Wespen en hoornaars vallen onder spoed-werk en worden los gefactureerd (geen contract-vereiste). Vraag een offerte op maat als u specifieke risico's heeft." },
    { q: "Wat als ik nu al een acute melding heb?",
      a: "Bel 085 500 50 50 voor directe spoeduitruk, ook in avond/weekend. Vanaf de uitruk kunnen wij meteen het preventiecontract bespreken voor de toekomst. Veel horeca-klanten zijn zo bij ons begonnen: eerst spoed, dan structureel." },
  ];

  useSEO({
    title: "Horeca & HACCP ongediertebestrijding op contract",
    description: "HACCP-conforme ongediertebestrijding voor restaurants, hotels en horeca. Vaste kwartaalcontroles, gecertificeerde aanpak, 24/7 spoed. Voorkom NVWA-boete of sluiting.",
    path: "/horeca",
    jsonLd: [
      buildBreadcrumb([{ name: "Home", url: "/" }, { name: "Horeca", url: "/horeca" }]),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": HORECA_FAQS.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
      // Service schema specifiek voor B2B / HACCP context
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "HACCP-conforme ongediertebestrijding voor horeca",
        "serviceType": "Preventiecontract ongedierte horeca",
        "provider": { "@id": "https://www.abcongedierte.nl/#organization" },
        "areaServed": [
          { "@type": "AdministrativeArea", "name": "Zuid-Holland" },
          { "@type": "AdministrativeArea", "name": "Noord-Holland" },
          { "@type": "AdministrativeArea", "name": "Utrecht" }
        ],
        "audience": { "@type": "BusinessAudience", "audienceType": "Restaurants, hotels, café's, catering, bedrijfskantines, foodtrucks" },
      },
    ],
  });

  const [pc, setPc] = useState("");
  const formatPc = (raw) => { let v = raw.replace(/\s/g, '').toUpperCase(); if (v.length > 4) v = v.slice(0,4)+' '+v.slice(4,6); return v; };
  const searchPostcode = () => { const r = pc.trim().replace(/\s/g,''); if (r.length < 4) { alert('Vul een geldige postcode in'); return; } navigate(`/offerte?pc=${encodeURIComponent(r)}`); };
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="page-anim">
      {/* HERO — zelfde structuur als andere hoofdpagina's */}
      <section className="hero">
        <div className="container">
          <div className="hero-inner">
            <div>
              <div className="breadcrumb" style={{ marginBottom: 18 }}>
                <a href={url.home()}>Home</a><span className="sep">/</span>
                <span style={{ color: 'rgba(255,255,255,.9)' }}>Horeca &amp; HACCP</span>
              </div>
              <div className="hero-badge">🍽 HACCP-conforme aanpak</div>
              <h1>
                Voor <em>horeca</em> &amp; <br/>
                <em>HACCP</em>-keukens
              </h1>
              <p className="hero-sub">
                Vaste kwartaalcontroles met vakkundige aanpak en 24/7 spoeddienst.
                Voorkom NVWA-boete of sluiting met een preventiecontract.
              </p>
              <HeroPostcodeBar pc={pc} setPc={setPc} formatPc={formatPc} searchPostcode={searchPostcode} />
              <div className="hero-rating-mobile"><RatingBar /></div>
              <div className="hero-trust">
                <div className="tit"><span className="tit-mark">✓</span> NVWA-conforme aanpak</div>
                <div className="tit"><span className="tit-mark">✓</span> 4× per jaar controle</div>
                <div className="tit"><span className="tit-mark">✓</span> 24/7 spoeddienst</div>
              </div>
              <div className="hero-rating-desktop"><RatingBar /></div>
            </div>
            <RotterdamForm variant="hero" />
          </div>
        </div>
      </section>

      {/* WAAROM HACCP — risico's */}
      <section className="section">
        <div className="container" style={{ maxWidth: 1040 }}>
          <div className="sh">
            <div className="section-tag">Waarom een contract?</div>
            <h2>Wat staat er op het spel <em>zonder</em> goede preventie?</h2>
          </div>
          <div className="info-card-grid" style={{ marginTop: 32 }}>
            {[
              { icon: '⚠', label: 'NVWA-boete', value: 'Tot €5.250', sub: 'Bij geconstateerde ongedierte-overlast in keuken of opslag' },
              { icon: '🚫', label: 'Tijdelijke sluiting', value: '1-14 dagen', sub: 'Bij ernstige overtredingen, complete omzetdaling' },
              { icon: '📉', label: 'Reputatieschade', value: 'Permanent', sub: 'Eén virale review of nieuwsbericht over plaag, jaren impact' },
              { icon: '📋', label: 'Verzekering keert niet uit', value: 'Vaak uitgesloten', sub: 'Schade door ongedierte valt buiten standaard polisdekking' },
              { icon: '👥', label: 'Personeel raakt ziek', value: 'Ratten/kakkerlakken', sub: 'Salmonella, leptospirose en andere ziekteverwekkers' },
              { icon: '💸', label: 'Spoed-uitruk per geval', value: 'Vaste hoge kosten', sub: 'Veel duurder dan een maandelijks contract met preventie' },
            ].map(c => (
              <div key={c.label} className="info-card info-card--time">
                <div className="info-card-icon" aria-hidden="true">{c.icon}</div>
                <div className="info-card-body">
                  <div className="info-card-label">{c.label}</div>
                  <div className="info-card-value">{c.value}</div>
                  <div className="info-card-sub">{c.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WAT KRIJG JE IN HET CONTRACT */}
      <section className="section section-bg">
        <div className="container" style={{ maxWidth: 1040 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
            <div style={{ background: 'white', borderRadius: 16, padding: 28, border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}>
              <h2 style={{ fontSize: '1.3rem', color: 'var(--navy)', margin: '0 0 14px' }}>Wat zit er in het preventiecontract?</h2>
              <ul style={{ color: 'var(--text)', lineHeight: 1.85, paddingLeft: 22, margin: 0 }}>
                <li><strong>4× per jaar visuele inspectie</strong> (keuken, opslag, kruipruimte, dakgoten, leidingschachten)</li>
                <li><strong>Mondelinge terugkoppeling</strong> na elk bezoek met alle bevindingen en aandachtspunten</li>
                <li><strong>Lokstations met monitoringkaart</strong> op strategische punten buiten voedselzones</li>
                <li><strong>CTGB-gecertificeerde middelen</strong> toegestaan voor levensmiddelenomgevingen</li>
                <li><strong>24/7 spoedlijn</strong> bij acute meldingen tussen bezoeken</li>
                <li><strong>Eerste-respons binnen 24 uur</strong> bij spoedaanvraag</li>
                <li><strong>Gecertificeerde bestrijders</strong> die de horeca-praktijk kennen</li>
              </ul>
            </div>
            <div style={{ background: 'var(--bg)', borderRadius: 16, padding: 28, border: '1px solid var(--border)' }}>
              <h2 style={{ fontSize: '1.3rem', color: 'var(--navy)', margin: '0 0 14px' }}>Voor welke horeca?</h2>
              <ul style={{ color: 'var(--text)', lineHeight: 1.85, paddingLeft: 22, margin: 0 }}>
                <li><strong>Restaurants</strong>, fine dining tot fastfood</li>
                <li><strong>Café's, bars en lounges</strong> met keuken of snackaanbod</li>
                <li><strong>Hotels</strong> met eigen restaurant of breakfast-buffet</li>
                <li><strong>Bedrijfskantines</strong> en zorgkantines (ziekenhuizen, scholen)</li>
                <li><strong>Catering-bedrijven</strong> met productieruimte</li>
                <li><strong>Foodtrucks</strong> en mobiele keukens (apart pakket)</li>
                <li><strong>Bakkers, slagers, viswinkels</strong> en speciaalzaken</li>
                <li><strong>Distributiecentra</strong> en groothandels met voedingsmiddelen</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PROCES — hoe gaat het */}
      <section className="section">
        <div className="container">
          <div className="sh">
            <div className="section-tag">Zo gaat het</div>
            <h2>Van intake tot HACCP-conform <em>in 4 stappen</em></h2>
          </div>
          <div className="contact-stappen" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18, marginTop: 24 }}>
            {[
              { n: 1, t: "Intake", d: "Bel of mail ons. Wij stellen vragen over pand-type, oppervlakte, eventuele bekende problemen en huidige situatie." },
              { n: 2, t: "Locatiebezoek", d: "Wij komen kosteloos langs voor een nul-meting: waar zitten risico's, welke acties zijn direct nodig, wat hoort in het contract." },
              { n: 3, t: "Offerte + contract", d: "U ontvangt een schriftelijke offerte met heldere contract-vorm, jaarbedrag en alle inbegrepen diensten. Geen verborgen kosten." },
              { n: 4, t: "Start preventie", d: "Vanaf akkoord: eerste bezoek binnen 1 week, lokstations geplaatst, vaste planning afgesproken. Vanaf dan loopt alles op vaste kwartaalbasis." },
            ].map(s => (
              <div key={s.n} style={{ background: 'white', borderRadius: 14, padding: 22, border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--orange)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '1rem', marginBottom: 12 }}>{s.n}</div>
                <h3 style={{ color: 'var(--navy)', fontSize: '1.02rem', margin: '0 0 8px' }}>{s.t}</h3>
                <p style={{ color: 'var(--text)', fontSize: '.92rem', lineHeight: 1.6, margin: 0 }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ ─ zelfde stijl als stad-pagina's */}
      <section className="section faq-section section-bg">
        <div className="container" style={{ maxWidth: 860 }}>
          <div className="sh">
            <div className="section-tag">Veelgestelde vragen</div>
            <h2>FAQ over <em>horeca &amp; HACCP</em></h2>
          </div>
          <div className="faq-list">
            {HORECA_FAQS.map((f, i) => (
              <div key={i} className={"faq-item" + (openFaq === i ? " open" : "")}>
                <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? -1 : i)} aria-expanded={openFaq === i}>
                  <span>{f.q}</span>
                  <span className="faq-icon" aria-hidden="true">+</span>
                </button>
                <div className="faq-answer"><p>{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA-BLOK */}
      <section className="section">
        <div className="container" style={{ maxWidth: 920 }}>
          <div className="cta-block-inner" style={{ textAlign: 'center' }}>
            <h2>Klaar voor een HACCP-proof <em>preventiecontract</em>?</h2>
            <p style={{ color: 'var(--text)', fontSize: '1.05rem', lineHeight: 1.65, maxWidth: '60ch', margin: '8px auto 24px' }}>
              Bel ons direct of vraag een gratis intake aan. Binnen 1 week heeft u uw eerste contract-bezoek ingepland en is uw NVWA-administratie op orde.
            </p>
            <div className="cta-block-buttons" style={{ justifyContent: 'center' }}>
              <a href="tel:0855005050" className="cta-btn cta-btn-primary">
                <IconPhone width="18" height="18" /> 085 500 50 50
              </a>
              <a href={url.offerte()} className="cta-btn cta-btn-secondary">
                Vraag intake aan
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// PrivacyPage — AVG verplicht voor sites met formulieren / persoonsgegevens
// ──────────────────────────────────────────────────────────────────────
export function PrivacyPage() {
  useSEO({
    title: "Privacybeleid",
    description: "Hoe ABC Ongediertebestrijding uw persoonsgegevens verwerkt, conform AVG/GDPR. Lees over dataverwerking, bewaartermijnen, uw rechten en hoe u contact opneemt.",
    path: "/privacy",
    jsonLd: buildBreadcrumb([{ name: "Home", url: "/" }, { name: "Privacybeleid", url: "/privacy" }])
  });

  const sections = [
    { h: "1. Wie zijn wij?", p: "ABC Ongediertebestrijding b.v., Fuchsiastraat 55, 2565 PL Den Haag. KvK 71298592, BTW NL002334370B70. Bereikbaar via 085 500 50 50 en info@abcongedierte.nl." },
    { h: "2. Welke gegevens verwerken wij?", p: "Voor een offerte-aanvraag, contact of dienstverlening verwerken wij: naam, telefoonnummer, e-mailadres, postcode, en omschrijving van het ongediertesoort. Bij betaling: factuuradres en betalingsgegevens." },
    { h: "3. Waarom verwerken wij deze gegevens?", p: "(a) Om uw aanvraag te beantwoorden en een offerte te maken. (b) Voor de uitvoering van de overeenkomst (de bestrijding zelf, mogelijk uitgevoerd door onze gecertificeerde partners). (c) Voor wettelijke verplichtingen zoals belastingadministratie." },
    { h: "4. Hoe lang bewaren wij gegevens?", p: "Klantgegevens worden 7 jaar bewaard conform de fiscale bewaarplicht. Niet-omgezette offerte-aanvragen worden na 12 maanden verwijderd." },
    { h: "5. Met wie delen wij gegevens?", p: "Alleen met partijen die we strikt nodig hebben: onze boekhouder, onze gecertificeerde bestrijdings-partners (voor uitvoering van de behandeling) en betalingsverwerkers (Mollie/Stripe). Wij verkopen géén gegevens aan derden." },
    { h: "6. Uw rechten onder AVG", p: "U heeft het recht op inzage, correctie, verwijdering, beperking, en bezwaar tegen de verwerking van uw persoonsgegevens. Ook heeft u het recht om uw gegevens op te vragen (dataportabiliteit). Stuur uw verzoek naar info@abcongedierte.nl, wij reageren binnen 4 weken." },
    { h: "7. Cookies", p: "Wij gebruiken alleen functionele cookies die noodzakelijk zijn voor het werken van de site. Wij plaatsen geen tracking-cookies of advertentie-cookies zonder uw toestemming. Zie ons cookiebeleid voor details." },
    { h: "8. Beveiliging", p: "Wij beveiligen uw gegevens via HTTPS (TLS-encryptie), toegangsbeperking voor medewerkers, en moderne wachtwoord-praktijken. Bij een data-incident melden wij dit binnen 72 uur aan de Autoriteit Persoonsgegevens en aan u indien wettelijk verplicht." },
    { h: "9. Klachten", p: "Klacht over onze gegevensverwerking? Mail eerst naar info@abcongedierte.nl. Bent u niet tevreden? Dan kunt u een klacht indienen bij de Autoriteit Persoonsgegevens via autoriteitpersoonsgegevens.nl." },
    { h: "10. Wijzigingen", p: "Wij kunnen dit privacybeleid bijwerken. De laatste versie staat altijd op deze pagina. Significante wijzigingen kondigen wij minimaal 30 dagen vooraf aan." },
  ];

  return (
    <div className="page-anim">
      <section className="section" style={{ paddingTop: 56 }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <div className="breadcrumb" style={{ marginBottom: 18, color: 'var(--text-muted)' }}>
            <a href={url.home()}>Home</a><span className="sep" style={{ margin: '0 8px', opacity: .5 }}>/</span>
            <span style={{ color: 'var(--navy)', fontWeight: 600 }}>Privacybeleid</span>
          </div>
          <h1 style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.4rem)', color: 'var(--navy)', marginBottom: 8 }}>Privacybeleid</h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: 36, fontSize: '.92rem' }}>
            Laatst bijgewerkt: 19 mei 2026 · Conform Algemene Verordening Gegevensbescherming (AVG / GDPR)
          </p>
          {sections.map(s => (
            <div key={s.h} style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: '1.15rem', color: 'var(--navy)', marginBottom: 8 }}>{s.h}</h2>
              <p style={{ color: 'var(--text)', lineHeight: 1.75 }}>{s.p}</p>
            </div>
          ))}
          <div style={{ marginTop: 40, padding: 22, background: 'var(--bg)', borderRadius: 12, fontSize: '.92rem', color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--navy)' }}>Contact voor privacyvragen:</strong> info@abcongedierte.nl · 085 500 50 50
          </div>
        </div>
      </section>
    </div>);
}

// ──────────────────────────────────────────────────────────────────────
// CookiesPage — wij gebruiken alleen functionele cookies, geen consent nodig
// ──────────────────────────────────────────────────────────────────────
export function CookiesPage() {
  useSEO({
    title: "Cookiebeleid",
    description: "Welke cookies ABC Ongediertebestrijding gebruikt, alleen functionele cookies, geen tracking.",
    path: "/cookies",
    jsonLd: buildBreadcrumb([{ name: "Home", url: "/" }, { name: "Cookiebeleid", url: "/cookies" }])
  });

  return (
    <div className="page-anim">
      <section className="section" style={{ paddingTop: 56 }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <div className="breadcrumb" style={{ marginBottom: 18, color: 'var(--text-muted)' }}>
            <a href={url.home()}>Home</a><span className="sep" style={{ margin: '0 8px', opacity: .5 }}>/</span>
            <span style={{ color: 'var(--navy)', fontWeight: 600 }}>Cookiebeleid</span>
          </div>
          <h1 style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.4rem)', color: 'var(--navy)', marginBottom: 8 }}>Cookiebeleid</h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: 36, fontSize: '.92rem' }}>
            Laatst bijgewerkt: 19 mei 2026
          </p>
          <h2 style={{ fontSize: '1.15rem', color: 'var(--navy)', marginBottom: 8 }}>Wat zijn cookies?</h2>
          <p style={{ color: 'var(--text)', lineHeight: 1.75, marginBottom: 24 }}>
            Cookies zijn kleine tekstbestanden die websites op uw apparaat plaatsen om bepaalde functies mogelijk te maken. Onder de Telecommunicatiewet hoeven wij geen toestemming te vragen voor cookies die strikt noodzakelijk zijn voor het functioneren van de site.
          </p>
          <h2 style={{ fontSize: '1.15rem', color: 'var(--navy)', marginBottom: 8 }}>Welke cookies gebruiken wij?</h2>
          <p style={{ color: 'var(--text)', lineHeight: 1.75, marginBottom: 14 }}>
            <strong>1. Functionele cookies</strong> (altijd, technisch noodzakelijk):
          </p>
          <ul style={{ color: 'var(--text)', lineHeight: 1.85, marginBottom: 18, paddingLeft: 22 }}>
            <li>Formulier-state tijdens uw bezoek (om uw invoer niet te verliezen bij navigatie).</li>
            <li>Voorkeuren zoals taal en weergave-instellingen.</li>
            <li>Beveiligings-tokens (CSRF) bij het versturen van formulieren.</li>
            <li>Cookie-voorkeur (om uw keuze te onthouden).</li>
          </ul>
          <p style={{ color: 'var(--text)', lineHeight: 1.75, marginBottom: 14 }}>
            <strong>2. Analytische cookies</strong> (alleen met uw toestemming):
          </p>
          <ul style={{ color: 'var(--text)', lineHeight: 1.85, marginBottom: 24, paddingLeft: 22 }}>
            <li><strong>Google Analytics 4</strong> (cookies <code>_ga</code> en <code>_ga_*</code>). Wij gebruiken deze alleen om te begrijpen hoe bezoekers de site gebruiken (welke pagina's, hoe lang, welke route). IP-adressen worden geanonimiseerd. Bewaartermijn: 14 maanden. Cookies worden alléén geplaatst nadat u via de cookie-banner toestemming heeft gegeven.</li>
          </ul>
          <h2 style={{ fontSize: '1.15rem', color: 'var(--navy)', marginBottom: 8 }}>Wat doen wij NIET?</h2>
          <ul style={{ color: 'var(--text)', lineHeight: 1.85, marginBottom: 24, paddingLeft: 22 }}>
            <li>Wij plaatsen <strong>geen advertentie-cookies</strong> (geen Facebook Pixel, geen retargeting).</li>
            <li>Wij delen <strong>geen gegevens</strong> met derden voor marketing-doeleinden.</li>
            <li>Wij koppelen analyse-data <strong>niet</strong> aan uw e-mail of telefoonnummer.</li>
          </ul>
          <h2 style={{ fontSize: '1.15rem', color: 'var(--navy)', marginBottom: 8 }}>Toestemming intrekken</h2>
          <p style={{ color: 'var(--text)', lineHeight: 1.75, marginBottom: 14 }}>
            U kunt uw toestemming voor analytische cookies op elk moment intrekken via de knop hieronder. Daarna verwijderen wij de Google Analytics-cookies en sturen wij geen nieuwe data meer.
          </p>
          <button
            type="button"
            onClick={() => {
              try {
                localStorage.removeItem('abc-cookie-consent');
                // Verwijder GA cookies
                document.cookie.split(';').forEach((c) => {
                  const name = c.split('=')[0].trim();
                  if (name.startsWith('_ga') || name.startsWith('_gid')) {
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
                  }
                });
                alert('Uw cookie-keuze is gereset. Bij uw volgende bezoek krijgt u de cookie-banner opnieuw te zien.');
              } catch (e) { /* ignore */ }
            }}
            style={{
              padding: '10px 18px',
              background: 'var(--orange)',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: '0.92rem',
              cursor: 'pointer',
              marginBottom: 24,
            }}
          >
            Cookies resetten en opnieuw beslissen
          </button>
          <h2 style={{ fontSize: '1.15rem', color: 'var(--navy)', marginBottom: 8 }}>Cookies van derden</h2>
          <p style={{ color: 'var(--text)', lineHeight: 1.75, marginBottom: 24 }}>
            Onze contactpagina toont een Google Maps-iframe. Google kan daarbij eigen cookies plaatsen. Dit gebeurt alleen als u de pagina met de kaart bezoekt. Zie het privacybeleid van Google voor details.
          </p>
          <h2 style={{ fontSize: '1.15rem', color: 'var(--navy)', marginBottom: 8 }}>Cookies uitschakelen</h2>
          <p style={{ color: 'var(--text)', lineHeight: 1.75, marginBottom: 24 }}>
            U kunt cookies altijd verwijderen of blokkeren via uw browser-instellingen. Let op: zonder functionele cookies werken sommige onderdelen van de site mogelijk niet optimaal.
          </p>
          <div style={{ marginTop: 40, padding: 22, background: 'var(--bg)', borderRadius: 12, fontSize: '.92rem', color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--navy)' }}>Vragen?</strong> Mail naar info@abcongedierte.nl.
          </div>
        </div>
      </section>
    </div>);
}

// ──────────────────────────────────────────────────────────────────────
// VoorwaardenPage — Algemene voorwaarden (juridische basis)
// ──────────────────────────────────────────────────────────────────────
export function VoorwaardenPage() {
  useSEO({
    title: "Algemene voorwaarden",
    description: "Algemene voorwaarden van ABC Ongediertebestrijding b.v., geldend voor alle dienstverlening.",
    path: "/algemene-voorwaarden",
    jsonLd: buildBreadcrumb([{ name: "Home", url: "/" }, { name: "Algemene voorwaarden", url: "/algemene-voorwaarden" }])
  });

  const articles = [
    { t: "Artikel 1, Definities", p: "In deze voorwaarden: 'ABC' = ABC Ongediertebestrijding b.v. (KvK 71298592); 'Klant' = de natuurlijke of rechtspersoon die opdracht geeft; 'Diensten' = inspectie, advies en/of bestrijding van ongedierte." },
    { t: "Artikel 2, Toepasselijkheid", p: "Deze voorwaarden zijn van toepassing op elke offerte, opdracht en overeenkomst tussen ABC en Klant. Afwijkingen gelden alleen indien schriftelijk overeengekomen." },
    { t: "Artikel 3, Offertes en prijzen", p: "Offertes zijn vrijblijvend en 30 dagen geldig. Vermelde prijzen zijn inclusief BTW voor particulieren, exclusief BTW voor zakelijke klanten. Voorrijkosten zijn inbegrepen binnen ons werkgebied." },
    { t: "Artikel 4, Uitvoering van de overeenkomst", p: "ABC voert de diensten uit met de zorg die van een redelijk vakman mag worden verwacht. ABC werkt uitsluitend met IPM-gecertificeerde plaagdierspecialisten en CTGB-gecertificeerde bestrijdingsmiddelen." },
    { t: "Artikel 5, Resultaat en nacontrole", p: "Op wespenbestrijding geldt 100% seizoensgarantie: keert het nest binnen het seizoen terug, dan behandelen wij kosteloos opnieuw. Op alle overige ongediertesoorten geven wij geen resultaatgarantie; wij streven naar een vakkundige, duurzame aanpak en bieden waar relevant een nacontrole aan. Mocht het probleem terugkeren, bespreken wij samen met u de oorzaak (bv. nieuwe bouwkundige openingen, omgevingsfactoren) en stellen wij een vervolgaanpak voor." },
    { t: "Artikel 6, Betaling", p: "Tenzij anders overeengekomen: betaling à contant of binnen 14 dagen na factuurdatum. Bij overschrijding zijn wij gerechtigd wettelijke rente en incassokosten in rekening te brengen." },
    { t: "Artikel 7, Aansprakelijkheid", p: "ABC is aansprakelijk tot maximaal het bedrag van de overeenkomst. ABC is niet aansprakelijk voor: (a) gevolgschade; (b) schade door tekortkomingen van Klant in zijn medewerkingsplicht; (c) schade door overmacht zoals storm of overstroming." },
    { t: "Artikel 8, Klachten", p: "Klachten dient u binnen 14 dagen na constatering schriftelijk te melden via info@abcongedierte.nl. Wij reageren binnen 5 werkdagen." },
    { t: "Artikel 9, Toepasselijk recht", p: "Op alle overeenkomsten is Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter in het arrondissement Den Haag." },
  ];

  return (
    <div className="page-anim">
      <section className="section" style={{ paddingTop: 56 }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <div className="breadcrumb" style={{ marginBottom: 18, color: 'var(--text-muted)' }}>
            <a href={url.home()}>Home</a><span className="sep" style={{ margin: '0 8px', opacity: .5 }}>/</span>
            <span style={{ color: 'var(--navy)', fontWeight: 600 }}>Algemene voorwaarden</span>
          </div>
          <h1 style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.4rem)', color: 'var(--navy)', marginBottom: 8 }}>Algemene voorwaarden</h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: 36, fontSize: '.92rem' }}>
            ABC Ongediertebestrijding b.v. · KvK 71298592 · Versie: 19 mei 2026
          </p>
          {articles.map(a => (
            <div key={a.t} style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: '1.05rem', color: 'var(--navy)', marginBottom: 6 }}>{a.t}</h2>
              <p style={{ color: 'var(--text)', lineHeight: 1.75 }}>{a.p}</p>
            </div>
          ))}
          <div style={{ marginTop: 40, padding: 22, background: 'var(--bg)', borderRadius: 12, fontSize: '.92rem', color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--navy)' }}>Vragen?</strong> Mail naar info@abcongedierte.nl of bel 085 500 50 50.
          </div>
        </div>
      </section>
    </div>);
}

